import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  Paper,
  Avatar,
  IconButton,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DocumentIcon,
  CheckCircle as CheckCircleIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  InfoOutlined,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAssociates, updateAssociate } from '../../services/associate';
import {
  getDesignations,
  getBanks,
  getStates,
  getCities,
  getCommissions,
} from '../../services/master';
import { getToken } from '../../services/authAPIS';
import { viewUrl, uploadFileToS3, getSignedUrls } from '../../services/s3';
import AlertModal from '../../components/AlertModal';
import { hasWritePermission } from '../../utils/menuFilter';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function toDateOnly(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime()))
    return typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : '';
  return d.toISOString().slice(0, 10);
}

async function createAssociate(payload) {
  const res = await fetch(`${API_BASE}/api/associates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));

  if (!res.ok || body?.status === false) {
    const err = new Error(body?.message || `Create failed: ${res.status}`);
    if (Array.isArray(body?.errors)) err.errors = body.errors;
    throw err;
  }

  return body;
}

// Update associate with file URLs
async function updateAssociateFiles(associateId, fileUrls) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/associates/${associateId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(fileUrls),
  });
  const body = await res.json().catch(() => ({}));

  if (!res.ok || body?.status === false) {
    throw new Error(
      body?.message || `Failed to update associate files: ${res.status}`,
    );
  }

  return body;
}

function mergeSponsorLists(base = [], additions = []) {
  const seen = new Set();
  const merged = [];

  [...additions, ...base].forEach((item) => {
    if (!item) return;

    const code = (item.code ?? '').toString().trim();
    const name = (item.name ?? '').toString().trim();
    const mobile = (item.mobile ?? '').toString().trim();
    const id = item.id ?? item._id ?? null;
    const commission =
      typeof item.commission === 'number'
        ? item.commission
        : item.commission != null
          ? Number(item.commission)
          : undefined;
    const key = (code || name).toLowerCase();

    if (!key || seen.has(key)) {
      return;
    }

    merged.push({
      code: code || name,
      name: name || code,
      mobile,
      id,
      commission,
    });
    seen.add(key);
  });

  return merged;
}

const AssociateRegistration = () => {
  const history = useHistory();
  const location = useLocation();
  const canWriteAssociate =
    hasWritePermission('associate.associate-registration') ||
    hasWritePermission('associate-registration') ||
    hasWritePermission('associate.associate-list') ||
    hasWritePermission('associate');
  const canShowOldBusiness =
    hasWritePermission('associate.old-business') ||
    hasWritePermission('old-business');

  const [alertModal, setAlertModal] = useState({
    open: false,
    title: 'Alert',
    message: '',
  });
  const [confirmDesignationOpen, setConfirmDesignationOpen] = useState(false);

  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };

  const editingAssociate = location.state?.associate || null;
  const [activeStep, setActiveStep] = useState(0);

  const { user: loggedInUser } = useAuth();
  const loggedInUserRole = useMemo(() => {
    return (loggedInUser?.role || '').toLowerCase();
  }, [loggedInUser]);

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  const [addressProofError, setAddressProofError] = useState('');
  const [pincodeError, setPincodeError] = useState('');
  const [ifscError, setIfscError] = useState('');
  const [accountNumberError, setAccountNumberError] = useState('');
  const [nomineePhoneError, setNomineePhoneError] = useState('');
  const [altMobileError, setAltMobileError] = useState('');
  const [generatedLoginId, setGeneratedLoginId] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');

  const [step0Errors, setStep0Errors] = useState({});
  const [step1Errors, setStep1Errors] = useState({});
  const [step2Errors, setStep2Errors] = useState({});

  const [form, setForm] = useState({
    sponsorCode: '',
    registrationDate: toDateOnly(new Date()),
    branch: '',
    profileImage: null,
    name: '',
    fatherName: '',
    motherName: '',
    gender: '',
    marriedStatus: '',
    anniversary: '',
    dob: '',
    mobile: '',
    altMobile: '',
    email: '',
    address: '',
    pinCode: '',
    city: '',
    state: '',
    designation: '',
    addressProofType: '',
    addressProofNumber: '',
    addressProofFile: null,
    addressType: '',
    addressNumber: '',
    addressImage: null,
    drivingLicenceType: '',
    drivingLicenceNumber: '',
    drivingLicenceImage: null,
    voterIdType: '',
    voterIdNumber: '',
    voterIdImage: null,
    idProofNumber: '',
    idProofFile: null,
    bankDocNumber: '',
    bankDocFile: null,
    branch: '',
    terms: false,
    role: '',
    commission: '',
    sponsorName: '',
    sponsorId: '',
    sponsorDesignation: '',
    sponsorMobile: '',
    sponsorCommission: '',
    sponsorPermissionCount: null, // NEW: For role hierarchy filtering
    sponsorIdCard: '',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    nomineeName: '',
    nomineePhone: '',
    nomineeRelation: '',
    department: 'Sales',
    employeeType: 'Permanent',
    trialLastDate: '',
    deActivationDate: '',
    qualification: '',
    aadhar: '',
    pan: '',
    whatsapp: '',
    oldBusiness: '',
    totalBookedAmount: 0,
  });

  // State to store existing image URLs from the associate
  const [existingImageUrls, setExistingImageUrls] = useState({
    profileImageUrl: null, // from editingAssociate.img
    addressImageUrl: null, // from personalDetail[0].addressImage
    panImageUrl: null, // from personalDetail[0].panImage
    bankDocFileUrl: null, // from editingAssociate.bankDocFile
    drivingLicenseImageUrl: null, // from editingAssociate.drivingLicenseImage
    voterIdImageUrl: null, // from editingAssociate.voterIdImage
  });

  const [sponsorDirectory, setSponsorDirectory] = useState([]);
  const [sponsorSearchInput, setSponsorSearchInput] = useState('');
  const [commissionCap, setCommissionCap] = useState(null);
  const [loadingSponsors, setLoadingSponsors] = useState(false);

  const searchSponsors = async (searchTerm) => {
    setLoadingSponsors(true);
    let normalized = []; // Variable to store and return data

    try {
      // 1. Prepare Parameters
      // If search term exists, look for it. If not, fetch the top 20 (default view).
      const params = {
        page: 1,
        limit: searchTerm && searchTerm.length > 0 ? 10 : 20,
      };

      if (searchTerm && searchTerm.length > 0) {
        params.search = searchTerm;
      }

      // 2. API Call
      const response = await getAssociates(params);

      // 3. Normalize Data
      if (response?.status && Array.isArray(response?.data)) {
        const docs = response.data;
        normalized = docs
          .map((item) => {
            // Determine the Code (Readable ID)
            const codeCandidate =
              item?.userAccess?.loginId ??
              item?.loginId ??
              item?.associateId ??
              item?._id ??
              item?.id ??
              item?.code ??
              item?.email ??
              '';

            // Determine the Name
            const primaryName =
              [item?.name, item?.lastName].filter(Boolean).join(' ').trim() ||
              item?.email ||
              '';

            const code = String(codeCandidate || primaryName || '').trim();

            // Skip if no valid identifier found
            if (!code) return null;

            const designationValue =
              (typeof item?.designation === 'string'
                ? item.designation
                : item?.designation?.name ||
                  item?.designation?.designationName) ||
              (Array.isArray(item?.personalDetail) &&
                item.personalDetail[0]?.designation) ||
              (Array.isArray(item?.sponsorDetail) &&
                item.sponsorDetail[0]?.designation) ||
              '';

            const commissionValue =
              item?.commission ??
              (Array.isArray(item?.sponsorDetail) &&
                item.sponsorDetail[0]?.commission) ??
              item?.sponsorInfo?.sponsorCommission ??
              '';

            return {
              id: item?._id || item?.id || null, // MongoDB ID (Used for linking)
              code: code, // Readable ID (Displayed in dropdown)
              name: primaryName || code,
              mobile: (item?.phone ?? item?.whatsapp ?? '').toString().trim(),
              designation: String(designationValue), // Ensure it's a string
              commission: commissionValue,
              role: item?.userAccess?.role || item?.role || '',
            };
          })
          .filter(Boolean); // Remove nulls

        // 4. Update State
        setSponsorDirectory(normalized);
      }
    } catch (error) {
      console.error('Failed to search sponsors:', error);
      setSponsorDirectory([]);
    } finally {
      setLoadingSponsors(false);
    }

    // 5. CRITICAL: Return the data so useEffect can read it immediately
    return normalized;
  };

  const [pinDirectory] = useState([
    { pincode: '560066', city: 'Bangalore', state: 'Karnataka' },
    { pincode: '400001', city: 'Mumbai', state: 'Maharashtra' },
    { pincode: '110001', city: 'Delhi', state: 'Delhi' },
    { pincode: '600001', city: 'Chennai', state: 'Tamil Nadu' },
    { pincode: '700001', city: 'Kolkata', state: 'West Bengal' },
  ]);
  const uniqueStates = Array.from(new Set(pinDirectory.map((p) => p.state)));

  const [designations, setDesignations] = useState([]);
  const [banks, setBanks] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [roles, setRoles] = useState([]);
  const [commissionOptionsFromAPI, setCommissionOptionsFromAPI] = useState([]);

  const [addCityDialogOpen, setAddCityDialogOpen] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [addingCity, setAddingCity] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewImageTitle, setPreviewImageTitle] = useState('');
  const [previewImageError, setPreviewImageError] = useState(false);
  const [previewImageLoading, setPreviewImageLoading] = useState(true);
  const filteredCities = cities.filter(
    (city) => !form.state || city.state === form.state,
  );

  const steps = ['Personal Information', 'Contact Details', 'KYC Documents'];

  const validatePanNumber = (pan) => {
    if (!pan) return '';

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

    if (pan.length !== 10) {
      return 'PAN must be exactly 10 characters';
    }

    if (!panRegex.test(pan)) {
      return 'Invalid PAN format. Must be AAAAA9999A (5 letters, 4 digits, 1 letter)';
    }

    return '';
  };

  const validateAadharNumber = (aadhar) => {
    if (!aadhar) return '';

    const cleanAadhar = aadhar.replace(/\s/g, '');

    const aadharRegex = /^\d{12}$/;

    if (cleanAadhar.length !== 12) {
      return 'Aadhar must be exactly 12 digits';
    }

    if (!aadharRegex.test(cleanAadhar)) {
      return 'Invalid Aadhar format. Must contain only digits';
    }

    return '';
  };

  const validateDrivingLicense = (dl) => {
    if (!dl) return '';

    // Standard Indian DL format: SS-RR-YYYY-NNNNNNN
    // Total 15 characters including state code and numbers
    const dlRegex = /^[A-Z]{2}\d{2}\d{4}\d{7}$/;
    const cleanDL = dl.replace(/[-\s]/g, '').toUpperCase();

    if (cleanDL.length !== 15) {
      return 'Driving License must be exactly 15 characters (e.g., DL0420110123456)';
    }

    if (!dlRegex.test(cleanDL)) {
      return 'Invalid Driving License format. Expected: SS RR YYYY NNNNNNN';
    }

    return '';
  };

  const validateVoterId = (voterId) => {
    if (!voterId) return '';

    // Standard EPIC Number format: 3 uppercase letters followed by 7 serial digits
    const voterIdRegex = /^[A-Z]{3}\d{7}$/;
    const cleanVoterId = voterId.replace(/[-\s\/]/g, '').toUpperCase();

    if (cleanVoterId.length !== 10) {
      return 'Voter ID (EPIC) must be exactly 10 characters (e.g., ABC1234567)';
    }

    if (!voterIdRegex.test(cleanVoterId)) {
      return 'Invalid Voter ID format. Expected: 3 letters followed by 7 digits';
    }

    return '';
  };

  const validatePincode = (pinCode) => {
    if (!pinCode) return '';

    if (pinCode.length !== 6) {
      return 'Pincode must be exactly 6 digits';
    }

    if (!/^\d{6}$/.test(pinCode)) {
      return 'Pincode must contain only digits';
    }

    return '';
  };

  const validateIfscCode = (ifsc) => {
    if (!ifsc) return '';

    const ifscRegex = /^[A-Z]{4}[A-Z0-9]{7}$/;

    if (ifsc.length !== 11) {
      return 'IFSC code must be exactly 11 characters';
    }

    if (!ifscRegex.test(ifsc.toUpperCase())) {
      return 'Invalid IFSC format. Must be 4 letters followed by 7 alphanumeric characters';
    }

    return '';
  };

  const validateAccountNumber = (accountNumber) => {
    if (!accountNumber) return '';

    if (accountNumber.length < 8 || accountNumber.length > 18) {
      return 'Account number must be 8-18 digits';
    }

    if (!/^\d+$/.test(accountNumber)) {
      return 'Account number must contain only digits';
    }

    return '';
  };

  const getAddressProofHelperText = (type) => {
    switch (type) {
      case 'Aadhar':
        return 'Format: 12 digits (XXXX XXXX XXXX)';
      case 'DL':
        return 'Format: SSRRYYYYNNNNNNN (e.g., DL0420110123456)';
      case 'VoterId':
        return 'Format: XXXNNNNNNN (e.g., ABC1234567)';
      default:
        return 'Enter document number';
    }
  };

  const generateLoginId = (name) => {
    if (!name || name.trim().length === 0) return '';

    const namePart = name
      .trim()
      .replace(/[^a-zA-Z]/g, '')
      .substring(0, 4)
      .toLowerCase();

    const randomDigits = Math.floor(1000 + Math.random() * 9000).toString();

    return namePart + randomDigits;
  };

  const generatePassword = (name) => {
    if (!name || name.trim().length === 0) return '';

    const namePart = name
      .trim()
      .replace(/[^a-zA-Z]/g, '')
      .substring(0, 4)
      .toLowerCase();

    const specialChars = ['!', '@', '#', '$', '%', '&', '*'];
    const specialChar =
      specialChars[Math.floor(Math.random() * specialChars.length)];

    const randomDigits = Math.floor(1000 + Math.random() * 9000).toString();

    return namePart + specialChar + randomDigits;
  };

  const clearFieldError = (fieldName) => {
    if (activeStep === 0) {
      setStep0Errors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    } else if (activeStep === 1) {
      setStep1Errors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    } else if (activeStep === 2) {
      setStep2Errors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);

    if (field === 'addressProofNumber' || field === 'addressProofType') {
      setAddressProofError('');
    }

    if (field === 'pincode') {
      setPincodeError('');
    }

    if (field === 'accountNumber') {
      setAccountNumberError('');
    }

    if (field === 'ifscCode') {
      setIfscError('');
    }
  };

  const handleAddressProofChange = (field, value) => {
    if (field === 'addressProofType') {
      const typeField =
        value === 'Aadhar'
          ? 'addressType'
          : value === 'DL'
            ? 'drivingLicenceType'
            : value === 'VoterId'
              ? 'voterIdType'
              : '';
      setForm((prev) => ({
        ...prev,
        addressProofType: value,
        [typeField]: value,
      }));
      clearFieldError('addressProofType');
      setAddressProofError('');
    } else if (field === 'addressProofNumber') {
      let finalValue = value;
      if (form.addressProofType === 'Aadhar') {
        finalValue = value.replace(/\D/g, '').slice(0, 12);
      } else if (form.addressProofType === 'DL') {
        finalValue = value.replace(/[-\s]/g, '').toUpperCase().slice(0, 15);
      } else if (form.addressProofType === 'VoterId') {
        finalValue = value
          .replace(/[-\s\/]/g, '')
          .toUpperCase()
          .slice(0, 10);
      }

      const numberField =
        form.addressProofType === 'Aadhar'
          ? 'addressNumber'
          : form.addressProofType === 'DL'
            ? 'drivingLicenceNumber'
            : form.addressProofType === 'VoterId'
              ? 'voterIdNumber'
              : 'addressProofNumber';

      setForm((prev) => ({
        ...prev,
        addressProofNumber: finalValue,
        [numberField]: finalValue,
      }));

      let validationError = '';
      if (form.addressProofType === 'Aadhar') {
        validationError = validateAadharNumber(finalValue);
      } else if (form.addressProofType === 'DL') {
        validationError = validateDrivingLicense(finalValue);
      } else if (form.addressProofType === 'VoterId') {
        validationError = validateVoterId(finalValue);
      }

      setAddressProofError(validationError);
    }
  };

  const handleCloseAddCityDialog = () => {
    setAddCityDialogOpen(false);
    setNewCityName('');
  };

  const handleOpenImagePreview = async (urlOrKey, title) => {
    setPreviewImageTitle(title);
    setPreviewImageError(false);
    setPreviewImageLoading(true);
    setImagePreviewOpen(true);

    // viewUrl will automatically extract S3 key from full S3 URLs and call the API
    const finalUrl = await viewUrl(urlOrKey);

    if (!finalUrl) {
      setPreviewImageError(true);
      setPreviewImageLoading(false);
      return showAlert('Failed to load image preview');
    }

    setPreviewImageUrl(finalUrl);
    setPreviewImageLoading(false);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImageUrl(null);
    setPreviewImageTitle('');
    setPreviewImageError(false);
    setPreviewImageLoading(true);
  };

  const handleDownloadImage = () => {
    if (!previewImageUrl) return;

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = previewImageUrl;
      // Extract file extension from URL
      const urlParts = previewImageUrl.split('.');
      const extension =
        urlParts.length > 1
          ? urlParts[urlParts.length - 1].split('?')[0]
          : 'jpg';
      link.download = `${previewImageTitle || 'image'}.${extension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download image:', error);
      // Fallback: open in new tab
      window.open(previewImageUrl, '_blank');
    }
  };

  const handleAddCity = async () => {
    if (!newCityName.trim() || !form.state) {
      return;
    }

    setAddingCity(true);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'city',
          details: {
            cityName: newCityName.trim(),
            state: form.state,
          },
          status: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add city');
      }

      const result = await response.json();

      const newCity = {
        id: result.data?._id || Date.now().toString(),
        name: newCityName.trim(),
        value: newCityName.trim(),
        state: form.state,
      };

      setCities((prev) => [...prev, newCity]);
      setForm((prev) => ({ ...prev, city: newCity.value }));
      handleCloseAddCityDialog();
    } catch (error) {
      console.error('Error adding city:', error);
    } finally {
      setAddingCity(false);
    }
  };

  const handleFileChange = (field, file) => {
    if (file) {
      let maxSize;
      if (field === 'profileImage') {
        maxSize = 2 * 1024 * 1024;
      } else {
        maxSize = 5 * 1024 * 1024;
      }

      if (file.size > maxSize) {
        const sizeLimit = field === 'profileImage' ? '2MB' : '5MB';
        setError(
          `File size exceeds ${sizeLimit} limit. Please choose a smaller file.`,
        );
        return;
      }

      if (field === 'addressProofFile') {
        const imageField =
          form.addressProofType === 'Aadhar'
            ? 'addressImage'
            : form.addressProofType === 'DL'
              ? 'drivingLicenceImage'
              : form.addressProofType === 'VoterId'
                ? 'voterIdImage'
                : 'addressProofFile';
        setForm((prev) => ({
          ...prev,
          addressProofFile: file,
          [imageField]: file,
        }));
        // Clear existing address image URL when new file is uploaded
        if (form.addressProofType === 'Aadhar') {
          setExistingImageUrls((prev) => ({ ...prev, addressImageUrl: null }));
        }
      } else if (field === 'profileImage') {
        setForm((prev) => ({ ...prev, [field]: file }));
        // Clear existing profile image URL when new file is uploaded
        setExistingImageUrls((prev) => ({ ...prev, profileImageUrl: null }));
      } else if (field === 'idProofFile') {
        setForm((prev) => ({ ...prev, [field]: file }));
        // Clear existing PAN image URL when new file is uploaded
        setExistingImageUrls((prev) => ({ ...prev, panImageUrl: null }));
      } else {
        setForm((prev) => ({ ...prev, [field]: file }));
      }
      setError('');
    }
  };

  const handleRemoveFile = (field) => {
    if (field === 'addressProofFile') {
      const imageField =
        form.addressProofType === 'Aadhar'
          ? 'addressImage'
          : form.addressProofType === 'DL'
            ? 'drivingLicenceImage'
            : form.addressProofType === 'VoterId'
              ? 'voterIdImage'
              : 'addressProofFile';
      setForm((prev) => ({
        ...prev,
        addressProofFile: null,
        [imageField]: null,
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleDownloadExistingImage = (imageUrl, imageTitle) => {
    if (!imageUrl) return;

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = imageUrl;
      // Extract file extension from URL
      const urlParts = imageUrl.split('.');
      const extension =
        urlParts.length > 1
          ? urlParts[urlParts.length - 1].split('?')[0]
          : 'jpg';
      link.download = `${imageTitle || 'image'}.${extension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download image:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  };

  const handleRemoveExistingImage = (imageType) => {
    // Clear the existing image URL
    if (imageType === 'profileImage') {
      setExistingImageUrls((prev) => ({ ...prev, profileImageUrl: null }));
    } else if (imageType === 'addressImage') {
      setExistingImageUrls((prev) => ({ ...prev, addressImageUrl: null }));
    } else if (imageType === 'panImage') {
      setExistingImageUrls((prev) => ({ ...prev, panImageUrl: null }));
    } else if (imageType === 'bankDocFile') {
      setExistingImageUrls((prev) => ({ ...prev, bankDocFileUrl: null }));
    } else if (imageType === 'drivingLicenseImage') {
      setExistingImageUrls((prev) => ({
        ...prev,
        drivingLicenseImageUrl: null,
      }));
    } else if (imageType === 'voterIdImage') {
      setExistingImageUrls((prev) => ({ ...prev, voterIdImageUrl: null }));
    }
  };

  const getFileIcon = (file) => {
    if (!file) return null;
    const fileType = file.type || '';
    if (fileType.includes('image')) return <ImageIcon />;
    if (fileType.includes('pdf')) return <PdfIcon />;
    return <DocumentIcon />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const validateEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMobile = (mobile) => {
    if (!mobile) return false;
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  const handleEmailChange = (value) => {
    setForm((prev) => ({ ...prev, email: value }));

    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleMobileChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => {
      const newForm = { ...prev, mobile: digitsOnly };

      if (!prev.whatsapp || prev.whatsapp === prev.mobile) {
        newForm.whatsapp = digitsOnly;
      }

      return newForm;
    });

    if (digitsOnly && digitsOnly.length < 10) {
      setMobileError('Mobile number must be 10 digits');
    } else if (digitsOnly && !validateMobile(digitsOnly)) {
      setMobileError('Mobile number invalid');
    } else {
      setMobileError('');
    }
  };

  const handleWhatsappChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => ({ ...prev, whatsapp: digitsOnly }));

    if (digitsOnly && digitsOnly.length < 10) {
      setWhatsappError('WhatsApp number must be 10 digits');
    } else if (digitsOnly && !validateMobile(digitsOnly)) {
      setWhatsappError('WhatsApp number invalid');
    } else {
      setWhatsappError('');
    }
  };

  const handleAltMobileChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => ({ ...prev, altMobile: digitsOnly }));

    if (digitsOnly && digitsOnly.length < 10) {
      setAltMobileError('Mobile number must be 10 digits');
    } else if (digitsOnly && !validateMobile(digitsOnly)) {
      setAltMobileError('Mobile number invalid');
    } else {
      setAltMobileError('');
    }
  };

  // Consolidated useEffect for EDIT Mode
  useEffect(() => {
    const initializeEditForm = async () => {
      if (!editingAssociate) return;
      setLoading(true);
      try {
        // 1. Basic Data Extraction
        const reg =
          Array.isArray(editingAssociate.registrationDetail) &&
          editingAssociate.registrationDetail[0]
            ? editingAssociate.registrationDetail[0]
            : {};
        const personal =
          Array.isArray(editingAssociate.personalDetail) &&
          editingAssociate.personalDetail[0]
            ? editingAssociate.personalDetail[0]
            : {};
        const bank =
          Array.isArray(editingAssociate.bankDetail) &&
          editingAssociate.bankDetail[0]
            ? editingAssociate.bankDetail[0]
            : {};
        const other =
          Array.isArray(editingAssociate.otherDetail) &&
          editingAssociate.otherDetail[0]
            ? editingAssociate.otherDetail[0]
            : {};
        const nominee =
          Array.isArray(editingAssociate.nomineeDetail) &&
          editingAssociate.nomineeDetail[0]
            ? editingAssociate.nomineeDetail[0]
            : {};
        const existingBankDocSource =
          bank.bankDocFileViewUrl ||
          editingAssociate.bankDocFileViewUrl ||
          bank.bankDocFileUrl ||
          bank.bankDocFile ||
          editingAssociate.bankDocFile ||
          '';

        const sponsorNode =
          Array.isArray(editingAssociate.sponsorDetail) &&
          editingAssociate.sponsorDetail[0]
            ? editingAssociate.sponsorDetail[0]
            : editingAssociate.sponsorInfo || {};

        const normalizeEnum = (val, options) => {
          if (!val) return '';
          const lower = val.toString().trim().toLowerCase();
          const match = options.find((opt) => opt.toLowerCase() === lower);
          return match || '';
        };

        // 2. Identify Sponsor IDs
        // Prefer the readable ID (e.g., "13") for searching
        const sponsorReadableId =
          sponsorNode.associateId ||
          sponsorNode.sponsorId ||
          editingAssociate.sponsorCode ||
          '';
        const sponsorMongoId =
          editingAssociate.sponsorBy || sponsorNode._id || '';

        // 3. FETCH SPONSOR DETAILS FROM API (To get Designation & Commission)
        const sponsorDesignationRaw =
          sponsorNode.designation || sponsorNode.sponsorDesignation || '';
        let finalSponsorDesignation =
          typeof sponsorDesignationRaw === 'string'
            ? sponsorDesignationRaw
            : sponsorDesignationRaw?.name || '';

        let finalSponsorCommission =
          sponsorNode.sponsorCommission ?? sponsorNode.commission ?? '';
        let finalSponsorMobile =
          sponsorNode.sponsorMobile || sponsorNode.mobile || '';

        if (sponsorReadableId) {
          // Call our updated searchSponsors function immediately
          const results = await searchSponsors(String(sponsorReadableId));

          // Find the specific sponsor in the results
          const match = results.find(
            (s) =>
              String(s.code) === String(sponsorReadableId) ||
              String(s.id) === String(sponsorMongoId),
          );

          if (match) {
            console.log('Matched Full Sponsor Data:', match);
            finalSponsorDesignation = match.designation || '';
            finalSponsorCommission = match.commission || '';
            finalSponsorMobile = match.mobile || finalSponsorMobile;

            // CRITICAL: Set the Cap so the dropdown validates correctly
            setCommissionCap(Number(match.commission));

            // NEW: Set Sponsor Permission Count for role filtering in Edit mode
            if (match.role && roles.length > 0) {
              const sponsorRoleData = roles.find(
                (r) => r.value.toLowerCase() === match.role.toLowerCase(),
              );
              if (sponsorRoleData)
                setForm((fp) => ({
                  ...fp,
                  sponsorPermissionCount: sponsorRoleData.permissionCount,
                }));
            }
          }
        }

        // 4. Store existing image URLs and fetch signed URLs if needed
        const loadImageUrl = async (urlOrKey) => {
          if (
            !urlOrKey ||
            typeof urlOrKey !== 'string' ||
            urlOrKey.trim() === ''
          )
            return null;

          try {
            // Use viewUrl to get signed URL - it handles both S3 keys and full S3 URLs
            const signedUrl = await viewUrl(urlOrKey);
            return signedUrl || urlOrKey; // Fallback to original if viewUrl returns null
          } catch (error) {
            console.error('Failed to load image URL:', error);
            // If it's already a full URL, return it; otherwise return null
            return urlOrKey.startsWith('http://') ||
              urlOrKey.startsWith('https://')
              ? urlOrKey
              : null;
          }
        };

        // Load all image URLs asynchronously
        const [profileUrl, addressUrl, panUrl, bankDocUrl, dlUrl, voterUrl] =
          await Promise.all([
            loadImageUrl(editingAssociate.img),
            loadImageUrl(personal.addressImage),
            loadImageUrl(personal.panImage),
            loadImageUrl(existingBankDocSource),
            loadImageUrl(editingAssociate.drivingLicenseImage),
            loadImageUrl(editingAssociate.voterIdImage),
          ]);

        // Debug logging
        console.log('Extracted image URLs:', {
          profileImageUrl: profileUrl,
          addressImageUrl: addressUrl,
          panImageUrl: panUrl,
          bankDocFileUrl: bankDocUrl,
          drivingLicenseImageUrl: dlUrl,
          voterIdImageUrl: voterUrl,
          raw: {
            img: editingAssociate.img,
            addressImage: personal.addressImage,
            panImage: personal.panImage,
            bankDocFile: existingBankDocSource,
            drivingLicenseImage: editingAssociate.drivingLicenseImage,
            voterIdImage: editingAssociate.voterIdImage,
          },
        });

        setExistingImageUrls({
          profileImageUrl: profileUrl,
          addressImageUrl: addressUrl,
          panImageUrl: panUrl,
          bankDocFileUrl: bankDocUrl,
          drivingLicenseImageUrl: dlUrl,
          voterIdImageUrl: voterUrl,
        });

        if (editingAssociate.userAccess) {
          setGeneratedLoginId(editingAssociate.userAccess.loginId || '');
          let existingPassword = editingAssociate.userAccess.password;
          if (!existingPassword) {
            if (reg && reg.password) {
              existingPassword = reg.password;
            } else if (editingAssociate.password) {
              existingPassword = editingAssociate.password;
            }
          }
          setGeneratedPassword(existingPassword || '');
        }

        setForm((prev) => ({
          ...prev,
          // --- Sponsor Fields ---
          sponsorCode: sponsorReadableId
            ? sponsorReadableId.toString()
            : prev.sponsorCode,
          sponsorId: sponsorMongoId
            ? sponsorMongoId.toString()
            : prev.sponsorId,
          sponsorName:
            sponsorNode.sponsorName ||
            editingAssociate.sponsorName ||
            prev.sponsorName,
          sponsorDesignation: finalSponsorDesignation, // Now populated from API
          sponsorCommission: finalSponsorCommission, // Now populated from API
          sponsorMobile: finalSponsorMobile,

          // --- Rest of the fields ---
          registrationDate: reg.registrationDate || prev.registrationDate,
          deActivationDate: reg.deActivationDate || prev.deActivationDate,
          name: editingAssociate.name || prev.name,
          fatherName: personal.fatherName || prev.fatherName,
          motherName: personal.motherName || prev.motherName,
          gender: normalizeEnum(personal.gender || prev.gender, [
            'Male',
            'Female',
            'Other',
          ]),
          marriedStatus: normalizeEnum(
            personal.marriedStatus || prev.marriedStatus,
            ['Single', 'Married'],
          ),
          anniversary: personal.anniversary || prev.anniversary,
          dob: personal.dob || prev.dob,
          qualification: personal.qualification || prev.qualification,
          mobile: (
            editingAssociate.phone ||
            editingAssociate.mobile ||
            ''
          ).toString(),
          whatsapp: (
            editingAssociate.whatsapp ||
            editingAssociate.phone ||
            editingAssociate.mobile ||
            ''
          ).toString(),
          email: editingAssociate.email || prev.email,
          address: editingAssociate.address || prev.address,
          city: editingAssociate.city || prev.city,
          state: editingAssociate.state || prev.state,
          pinCode: editingAssociate.pinCode || prev.pinCode,
          designation: editingAssociate.designation || prev.designation,
          role: prev.role, // Will be set in separate useEffect after roles load

          commission: editingAssociate.commission ?? prev.commission, // Associate's own commission

          addressProofType: personal.AddressType || prev.addressProofType,
          addressProofNumber: personal.addressNumber || prev.addressProofNumber,
          addressType: personal.AddressType || prev.addressType,
          addressNumber: personal.addressNumber || prev.addressNumber,
          idProofNumber: personal.pan || prev.idProofNumber,
          pan: personal.pan || prev.pan,
          aadhar: personal.addressNumber || prev.aadhar,
          bankName: bank.bankName || bank.bank || prev.bankName,
          accountHolderName: bank.accountName || prev.accountHolderName,
          accountNumber: bank.accountNumber || prev.accountNumber,
          branch: bank.branch || prev.branch,
          ifscCode: bank.ifscCode || bank.ifsc || prev.ifscCode,
          nomineeName: nominee.name || prev.nomineeName,
          nomineePhone: nominee.phone || prev.nomineePhone,
          nomineeRelation: nominee.relation || prev.nomineeRelation,
          employeeType: other.employeeType || prev.employeeType,
          department: other.department || prev.department,
          trialLastDate: other.trialLastDate || prev.trialLastDate,
          oldBusiness:
            editingAssociate.extraAmount ||
            editingAssociate.oldBookingAmount ||
            '',
          totalBookedAmount: editingAssociate.totalBookedAmount || 0,
        }));
      } finally {
        setLoading(false);
      }
    };

    initializeEditForm();
  }, [editingAssociate]);

  // Fix for merging the existing sponsor into the directory list
  // NEW: Automatically fetch full Sponsor details (Designation/Commission) on Edit
  // because the initial 'editingAssociate' object only has basic sponsor info.
  useEffect(() => {
    const fetchSponsorDetails = async () => {
      if (!editingAssociate) return;
      setLoading(true);
      try {
        // 1. Get the Sponsor's ID (Code) from the editing data
        const sponsorNode =
          Array.isArray(editingAssociate.sponsorDetail) &&
          editingAssociate.sponsorDetail[0]
            ? editingAssociate.sponsorDetail[0]
            : editingAssociate.sponsorInfo || {};

        // Prefer searching by the readable Associate ID (e.g., "13")
        const searchId =
          sponsorNode.associateId ||
          sponsorNode.sponsorId ||
          editingAssociate.sponsorCode;

        if (!searchId) return;

        try {
          // 2. Fetch the sponsor's full record from the server
          const response = await getAssociates({
            page: 1,
            limit: 5,
            search: String(searchId), // Search by ID (e.g., "13")
          });

          if (response?.status && Array.isArray(response?.data)) {
            // 3. Find the exact match in the results
            // We match by associateId (Code) OR the Mongo _id
            const sponsorData = response.data.find(
              (item) =>
                String(item.associateId) === String(searchId) ||
                item._id === String(editingAssociate.sponsorBy),
            );

            if (sponsorData) {
              console.log('Full Sponsor Details Found:', sponsorData);

              // 4. Update the form with the missing Designation and Commission
              setForm((prev) => ({
                ...prev,
                sponsorDesignation:
                  sponsorData.designation || prev.sponsorDesignation || '',
                sponsorCommission:
                  sponsorData.commission || prev.sponsorCommission || '',
                // Update name/mobile to ensure they are fresh
                sponsorName: sponsorData.name || prev.sponsorName,
                sponsorMobile: String(
                  sponsorData.phone ||
                    sponsorData.mobile ||
                    sponsorData.whatsapp ||
                    prev.sponsorMobile,
                ),
              }));

              // 5. Update the Directory so the Autocomplete works correctly
              setSponsorDirectory((prev) => {
                const enrichedEntry = {
                  id: sponsorData._id,
                  code: String(sponsorData.associateId || sponsorData._id),
                  name: sponsorData.name,
                  mobile: String(sponsorData.phone || sponsorData.mobile || ''),
                  designation: sponsorData.designation,
                  commission: sponsorData.commission,
                };
                return mergeSponsorLists(prev, [enrichedEntry]);
              });

              // 6. Set the commission cap for validation
              setCommissionCap(Number(sponsorData.commission));
            }
          }
        } catch (error) {
          console.error('Error fetching detailed sponsor info:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSponsorDetails();
  }, [editingAssociate]); // Removed 'roles' dependency here as we'll handle it below

  const handleCommissionChange = (value) => {
    let v = value;
    if (v === '') {
      setForm((prev) => ({ ...prev, commission: '' }));
      clearFieldError('commission');
      return;
    }
    let num = parseFloat(v);
    if (Number.isNaN(num)) return;
    if (num <= 0) num = 0.1;
    if (commissionCap != null && num > commissionCap) num = commissionCap;
    setForm((prev) => ({ ...prev, commission: num }));
    clearFieldError('commission');
  };

  const validateFieldStep0 = (field, value) => {
    let errorMsg = '';
    switch (field) {
      case 'registrationDate':
        if (!value) errorMsg = 'Registration Date is required';
        break;
      case 'sponsorCode':
        if (!value?.trim()) errorMsg = 'Sponsor is required';
        break;
      case 'name':
        if (!value?.trim()) errorMsg = 'Full Name is required';
        break;
      case 'dob':
        if (!value) errorMsg = 'Date of Birth is required';
        break;
      case 'commission':
        if (!value && value !== 0) errorMsg = 'Commission is required';
        break;
      case 'designation':
        if (!value) errorMsg = 'Designation is required';
        break;
      case 'role':
        if (!value) errorMsg = 'Role is required';
        break;
      case 'marriedStatus':
        if (!value) errorMsg = 'Marital Status is required';
        break;
      case 'gender':
        if (!value) errorMsg = 'Gender is required';
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlurStep0 = (field) => {
    const errorMsg = validateFieldStep0(field, form[field]);
    setStep0Errors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const validateFieldStep1 = (field, value) => {
    let errorMsg = '';
    switch (field) {
      case 'mobile':
        if (!value?.trim()) {
          errorMsg = 'Mobile Number is required';
        } else if (!validateMobile(value)) {
          errorMsg = 'Mobile number invalid';
        }
        break;
      case 'email':
        if (!value?.trim()) {
          errorMsg = 'Email is required';
        } else if (!validateEmail(value)) {
          errorMsg = 'Please enter a valid email address';
        }
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlurStep1 = (field) => {
    const errorMsg = validateFieldStep1(field, form[field]);
    setStep1Errors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const validateFieldStep2 = (field, value) => {
    let errorMsg = '';
    switch (field) {
      case 'idProofNumber':
        if (value?.trim()) {
          errorMsg = validatePanNumber(value);
        }
        break;
      case 'addressProofType':
        if (!value) {
          errorMsg = 'Address Proof Type is required';
        }
        break;
      case 'addressProofNumber':
        if (!value?.trim()) {
          errorMsg = `${form.addressProofType || 'Address Proof'} number is required`;
        } else {
          if (form.addressProofType === 'Aadhar') {
            errorMsg = validateAadharNumber(value);
          } else if (form.addressProofType === 'DL') {
            errorMsg = validateDrivingLicense(value);
          } else if (form.addressProofType === 'VoterId') {
            errorMsg = validateVoterId(value);
          }
        }
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlurStep2 = (field) => {
    const errorMsg = validateFieldStep2(field, form[field]);
    setStep2Errors((prev) => ({ ...prev, [field]: errorMsg }));
    if (field === 'addressProofNumber') {
      setAddressProofError(errorMsg);
    }
  };

  const validateStep0 = () => {
    const errors = {};

    if (!form.registrationDate) {
      errors.registrationDate = 'Registration Date is required';
    }
    if (!form.sponsorCode?.trim()) {
      errors.sponsorCode = 'Sponsor is required';
    }
    if (!form.name?.trim()) {
      errors.name = 'Full Name is required';
    }
    if (!form.dob) {
      errors.dob = 'Date of Birth is required';
    }
    if (!form.commission && form.commission !== 0) {
      errors.commission = 'Commission is required';
    }
    if (!form.designation) {
      errors.designation = 'Designation is required';
    }
    if (!form.role) {
      errors.role = 'Role is required';
    }
    if (!form.marriedStatus) {
      errors.marriedStatus = 'Marital Status is required';
    }
    if (!form.gender) {
      errors.gender = 'Gender is required';
    }
    if (form.nomineePhone && !validateMobile(form.nomineePhone)) {
      errors.nomineePhone = 'Nominee mobile number invalid';
    }

    return errors;
  };

  const validateStep1 = () => {
    const errors = {};

    if (!form.mobile?.trim()) {
      errors.mobile = 'Mobile Number is required';
    } else if (!validateMobile(form.mobile)) {
      errors.mobile = 'Mobile number invalid';
    }

    if (!form.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(form.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (form.whatsapp && !validateMobile(form.whatsapp)) {
      errors.whatsapp = 'WhatsApp number must be 10 digits and start with 6-9';
    }

    if (form.altMobile && !validateMobile(form.altMobile)) {
      errors.altMobile =
        'Alternate Mobile number must be 10 digits and start with 6-9';
    }

    if (form.pinCode && validatePincode(form.pinCode)) {
      errors.pinCode = validatePincode(form.pinCode);
    }

    if (form.accountNumber && validateAccountNumber(form.accountNumber)) {
      errors.accountNumber = validateAccountNumber(form.accountNumber);
    }

    if (form.ifscCode && validateIfscCode(form.ifscCode)) {
      errors.ifscCode = validateIfscCode(form.ifscCode);
    }

    return errors;
  };

  const validateStep2 = () => {
    const errors = {};

    if (form.idProofNumber?.trim()) {
      const panError = validatePanNumber(form.idProofNumber);
      if (panError) {
        errors.idProofNumber = panError;
      }
    }

    if (!form.addressProofType) {
      errors.addressProofType = 'Address Proof Type is required';
    } else if (!form.addressProofNumber?.trim()) {
      errors.addressProofNumber = `${form.addressProofType} number is required`;
    } else {
      let validationError = '';
      if (form.addressProofType === 'Aadhar') {
        validationError = validateAadharNumber(form.addressProofNumber);
      } else if (form.addressProofType === 'DL') {
        validationError = validateDrivingLicense(form.addressProofNumber);
      } else if (form.addressProofType === 'VoterId') {
        validationError = validateVoterId(form.addressProofNumber);
      }

      if (validationError) {
        errors.addressProofNumber = validationError;
      }
    }

    if (!form.terms) {
      errors.terms = 'Please accept the terms and conditions';
    }

    return errors;
  };

  const validateCurrentStep = (step) => {
    switch (step) {
      case 0:
        return validateStep0();
      case 1:
        return validateStep1();
      case 2:
        return validateStep2();
      default:
        return {};
    }
  };

  const handleNext = () => {
    // Designation Business Validation (Specific for Step 0)
    if (activeStep === 0 && canShowOldBusiness) {
      const isOldBusinessDisabled =
        !!editingAssociate && form.totalBookedAmount > 0;

      if (!isOldBusinessDisabled) {
        const selectedDesignationObj = designations.find(
          (d) => d.value === form.designation,
        );
        if (
          selectedDesignationObj &&
          (selectedDesignationObj.fromAmount || 0) > 0
        ) {
          const minRequiredBusiness = selectedDesignationObj.fromAmount;
          const currentOldBusiness = parseFloat(form.oldBusiness) || 0;

          if (currentOldBusiness < minRequiredBusiness) {
            // Auto-fill the required amount in the background silently
            form.oldBusiness = String(minRequiredBusiness);
          }
        }
      }
    }

    const validationErrors = validateCurrentStep(activeStep);

    setError('');

    switch (activeStep) {
      case 0:
        setStep0Errors(validationErrors);
        break;
      case 1:
        setStep1Errors(validationErrors);
        break;
      case 2:
        setStep2Errors(validationErrors);
        break;
      default:
        break;
    }

    if (Object.keys(validationErrors).length > 0) {
      const stepFieldLabels = {
        sponsorCode: 'Sponsor',
        registrationDate: 'Registration Date',
        name: 'Full Name',
        gender: 'Gender',
        marriedStatus: 'Marital Status',
        dob: 'Date of Birth',
        commission: 'Commission (%)',
        designation: 'Designation',
        role: 'Role',
        nomineePhone: 'Nominee Mobile Number',
        mobile: 'Mobile Number',
        email: 'Email Address',
        whatsapp: 'WhatsApp Number',
        altMobile: 'Alternate Mobile',
        pinCode: 'Pincode',
        accountNumber: 'Account Number',
        ifscCode: 'IFSC Code',
        idProofNumber: 'PAN Number',
        addressProofType: 'Address Proof Type',
        addressProofNumber: form.addressProofType
          ? `${form.addressProofType} Number`
          : 'Address Proof Number',
        terms: 'Terms and Conditions',
      };

      const missingFields = Object.keys(validationErrors).map(
        (field) => stepFieldLabels[field] || field,
      );
      showAlert(
        `Please fill in all required fields:\n${missingFields.join(', ')}`,
        'Required Fields',
      );

      let fieldOrder = [];
      if (activeStep === 0) {
        fieldOrder = [
          'sponsorCode',
          'registrationDate',
          'name',
          'gender',
          'marriedStatus',
          'dob',
          'designation',
          'commission',
          'role',
        ];
      } else if (activeStep === 1) {
        fieldOrder = ['mobile', 'email'];
      } else if (activeStep === 2) {
        fieldOrder = [
          'addressProofType',
          'addressProofNumber',
          'idProofNumber',
          'terms',
        ];
      }

      const firstErrorField = fieldOrder.find(
        (field) => validationErrors[field],
      );
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }, 100);
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      return;
    }

    if (activeStep === 0) {
      setConfirmDesignationOpen(true);
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleConfirmDesignation = () => {
    // Silent Auto-fill Old Business if it has a required minimum amount
    const selectedDesig = designations.find(
      (d) => d.value === form.designation,
    );
    if (
      selectedDesig &&
      (selectedDesig.fromAmount || selectedDesig.fromAmount === 0)
    ) {
      setForm((prev) => ({
        ...prev,
        oldBusiness: String(selectedDesig.fromAmount),
      }));
    }

    setConfirmDesignationOpen(false);
    setActiveStep((s) => s + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storedUser = window.localStorage.getItem('user');
      if (!storedUser) {
        return;
      }

      const parsedUser = JSON.parse(storedUser);
      const userRole = (parsedUser?.role || '').toLowerCase();
      if (userRole !== 'associate') {
        return;
      }

      const codeCandidate =
        parsedUser.associateCode ||
        parsedUser.associateId ||
        parsedUser.id ||
        parsedUser.userId ||
        parsedUser.code ||
        parsedUser.email ||
        parsedUser.phoneNumber ||
        parsedUser.mobile ||
        '';
      const nameCandidate =
        [parsedUser.firstName, parsedUser.lastName]
          .filter(Boolean)
          .join(' ')
          .trim() ||
        parsedUser.name ||
        parsedUser.email ||
        String(codeCandidate || '');
      const code = String(codeCandidate || nameCandidate || '').trim();
      if (!code) {
        return;
      }

      const entry = {
        id: parsedUser.id || parsedUser._id || parsedUser.userId,
        code,
        name: nameCandidate,
        mobile: (
          parsedUser.phoneNumber ||
          parsedUser.mobile ||
          parsedUser.phone ||
          ''
        ).toString(),
        role: parsedUser.role || '', // NEW
      };

      setSponsorDirectory((prev) => mergeSponsorLists(prev, [entry]));

      setSponsorSearchInput(
        (prev) =>
          prev ||
          (entry.name
            ? `${entry.name}${entry.mobile ? ` — ${entry.mobile}` : ''}`
            : entry.code),
      );
      setForm((prev) => {
        if (prev.sponsorCode) {
          return prev;
        }
        return {
          ...prev,
          sponsorCode: entry.code,
          sponsorName: entry.name,
          sponsorId: entry.id,
        };
      });
    } catch (error) {
      console.error('Failed to initialise sponsor from current user', error);
    }
  }, []); // Roles not needed here as it's separate Effect now

  useEffect(() => {
    const loadDesignations = async () => {
      setLoading(true);
      try {
        const token = getToken();
        console.log('Fetching designations with token:', !!token);
        const response = await fetch(
          `${API_BASE}/api/globalMasterDetails?type=designation`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );
        const body = await response.json().catch(() => ({}));

        if ((body?.status || body?.success) && Array.isArray(body?.data)) {
          const formatted = body.data
            .filter((item) => item.status)
            .map((item) => ({
              id: item._id,
              name: item.details?.designationName || item.globalMasterName,
              value: item.details?.designationName || item.globalMasterName,
              commission: item.details?.commission,
              level: Number(item.details?.level || 0),
              fromAmount: item.details?.fromAmount || 0,
            }));
          setDesignations(formatted);
        } else {
          // Fallback to service if needed or empty
          const fetchedDesignations = await getDesignations();
          setDesignations(fetchedDesignations);
        }
      } catch (error) {
        console.error('Failed to load designations', error);
        setDesignations([]);
      } finally {
        setLoading(false);
      }
    };

    loadDesignations();
  }, []);

  useEffect(() => {
    const loadBanks = async () => {
      setLoading(true);
      try {
        const fetchedBanks = await getBanks();
        setBanks(fetchedBanks);
      } finally {
        setLoading(false);
      }
    };

    loadBanks();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      setLoading(true);
      try {
        const fetchedStates = await getStates();
        setStates(fetchedStates);
      } finally {
        setLoading(false);
      }
    };

    loadStates();
  }, []);

  useEffect(() => {
    const loadCities = async () => {
      setLoading(true);
      try {
        const fetchedCities = await getCities();
        setCities(fetchedCities);
      } finally {
        setLoading(false);
      }
    };

    loadCities();
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      setLoading(true);
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE}/api/roleMasters`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data?.data)
          ? data.data.map((item) => ({
              id: item?._id || item?.id || item?.name,
              name: item?.name || 'N/A',
              value: item?.name || '',
              permissionCount: item?.permissionCount || 0, // NEW: Capture Permission Count
              roleType: item?.roleType || '', // NEW: Capture Role Type
            }))
          : [];
        setRoles(list);
      } catch (err) {
        console.error('Error fetching roles from roleMasters:', err);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    loadRoles();
  }, []);

  // Match and set role from editingAssociate after roles are loaded
  useEffect(() => {
    if (!editingAssociate || roles.length === 0) return;

    const roleValue =
      editingAssociate.userAccess?.role || editingAssociate.role;
    if (!roleValue) return;

    // Normalize role value to base form (e.g., "sales associate" -> "associate")
    const getBaseRole = (val) => {
      if (!val) return '';
      const normalized = val.toString().toLowerCase().trim();
      // Handle common variations - extract the base role name
      if (normalized.includes('associate')) return 'associate';
      if (normalized.includes('admin')) return 'admin';
      if (normalized.includes('manager')) return 'manager';
      if (normalized.includes('employee')) return 'employee';
      return normalized;
    };

    const apiRoleBase = getBaseRole(roleValue);

    // Find matching role from roles array
    const matchedRole = roles.find((role) => {
      const roleName = (role.value || role.name || '')
        .toString()
        .toLowerCase()
        .trim();
      const roleBase = getBaseRole(roleName);

      // Match by base role (e.g., "sales associate" matches "Associate")
      return roleBase === apiRoleBase;
    });

    if (matchedRole && matchedRole.value) {
      setForm((prev) => {
        // Only update if role is different to avoid unnecessary re-renders
        if (prev.role === matchedRole.value) return prev;
        return { ...prev, role: matchedRole.value };
      });
    }
  }, [editingAssociate, roles]);

  useEffect(() => {
    const fetchCommissions = async () => {
      setLoading(true);
      try {
        const commissions = await getCommissions();
        setCommissionOptionsFromAPI(commissions);
      } catch (error) {
        console.error('Failed to load commission options:', error);
        setCommissionOptionsFromAPI([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCommissions();
  }, []);

  useEffect(() => {
    const selectedText = form.sponsorName
      ? `${form.sponsorName}${form.sponsorMobile ? ` — ${form.sponsorMobile}` : ''}`
      : '';
    if (sponsorSearchInput !== selectedText) {
      const delayDebounceFn = setTimeout(() => {
        searchSponsors(sponsorSearchInput);
      }, 300);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [sponsorSearchInput, form.sponsorName, form.sponsorMobile]);

  useEffect(() => {
    if (form.sponsorCommission) {
      setCommissionCap(Number(form.sponsorCommission));
    } else {
      setCommissionCap(null);
    }
  }, [form.sponsorCommission]);

  useEffect(() => {
    // Skip login generation if we're editing and already have login credentials
    if (editingAssociate && editingAssociate.userAccess?.loginId) {
      return;
    }

    if (form.name && form.name.trim().length > 0) {
      const loginId = generateLoginId(form.name);
      const password = generatePassword(form.name);

      setGeneratedLoginId(loginId);
      setGeneratedPassword(password);
    } else {
      setGeneratedLoginId('');
      setGeneratedPassword('');
    }
  }, [form.name, editingAssociate]);

  // NEW: Dedicated Effect to handle Sponsor Role Permission Count calculation
  // This will run whenever the sponsorId changes (vía directory selection or initialization)
  // Or when roles list finally loads from API.
  useEffect(() => {
    if (!roles || roles.length === 0) return;

    const findSponsorPermissionCount = () => {
      // 1. Try to find the selected sponsor in the sponsorDirectory
      // (which we enriched with the 'role' field in searchSponsors)
      const selectedSponsor = sponsorDirectory.find(
        (s) => String(s.code || s.id) === String(form.sponsorCode),
      );

      let sponsorRole = selectedSponsor?.role;

      // 2. Fallback for Edge Cases (like initial load / edit mode)
      if (!sponsorRole) {
        // If we are in edit mode, maybe we can get it from editingAssociate
        if (editingAssociate) {
          const sponsorNode =
            Array.isArray(editingAssociate.sponsorDetail) &&
            editingAssociate.sponsorDetail[0]
              ? editingAssociate.sponsorDetail[0]
              : editingAssociate.sponsorInfo || {};
          sponsorRole = sponsorNode.role || editingAssociate.sponsorRole;
        }
      }

      // 3. Look up the role's permissionCount in the global roles list
      if (sponsorRole) {
        const roleData = roles.find(
          (r) => r.value.toLowerCase() === sponsorRole.toLowerCase(),
        );
        if (roleData) {
          setForm((prev) => ({
            ...prev,
            sponsorPermissionCount: roleData.permissionCount,
          }));
          return;
        }
      }

      // If we can't determine it, we just leave it as null (all roles shown)
    };

    findSponsorPermissionCount();
  }, [form.sponsorCode, roles, sponsorDirectory, editingAssociate]);

  function buildPayload(f) {
    return {
      loginId: generatedLoginId,
      password: generatedPassword,
      role: f.role?.toLowerCase() || 'associate',
      modules: [],
      img: '',
      sponsorId: f.sponsorId || f.sponsorCode || '',
      name: f.name,
      phone: f.mobile,
      email: f.email,
      designation: f.designation,
      city: f.city,
      state: f.state,
      commission: Number(f.commission || 0),
      whatsapp: f.whatsapp || f.mobile,
      address: f.address || '',
      pinCode: f.pinCode,
      extraAmount: parseFloat(f.oldBusiness) || 0,
      oldBookingAmount: f.oldBusiness || '',

      sponsorDetail: [
        {
          sponsorName: f.sponsorName || f.sponsorCode || '',
          designation: f.sponsorDesignation || '',
          idCard: f.sponsorIdCard || '',
        },
      ],

      registrationDetail: [
        {
          registrationDate:
            toDateOnly(f.registrationDate) || toDateOnly(new Date()),
          deActivationDate: toDateOnly(f.deActivationDate),
        },
      ],

      otherDetail: [
        {
          employeeType: f.employeeType || 'Permanent',
          department: f.department || 'Sales',
          trialLastDate: toDateOnly(f.trialLastDate),
        },
      ],

      personalDetail: [
        {
          fatherName: f.fatherName,
          motherName: f.motherName,
          dob: toDateOnly(f.dob),
          anniversary: toDateOnly(f.anniversary),
          gender: f.gender || '',
          marriedStatus: f.marriedStatus || '',
          qualification: f.qualification || '',
          aadhar: f.aadhar || '',
          pan: f.idProofNumber || f.pan || '',
          AddressType: f.addressProofType || '',
          addressNumber: f.addressProofNumber || '',
          addressImage: '',
          panImage: '',
        },
      ],

      bankDetail: [
        {
          accountName: f.accountHolderName || f.name,
          bank: f.bankName || '',
          accountNumber: f.bankDocNumber || f.accountNumber || '',

          ifsc: f.ifscCode || '',
          branch: f.branch || '',
          bankDocFile: f.bankDocFile || '',
        },
      ],

      nomineeDetail: [
        {
          name: f.nomineeName || '',
          phone: f.nomineePhone || '',
          relation: f.nomineeRelation || '',
        },
      ],

      status: true,
      isActive: true,
      createdBy: 'admin',
    };
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateStep2();
    setStep2Errors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const stepFieldLabels = {
        idProofNumber: 'PAN Number',
        addressProofType: 'Address Proof Type',
        addressProofNumber: form.addressProofType
          ? `${form.addressProofType} Number`
          : 'Address Proof Number',
        terms: 'Terms and Conditions',
      };

      const missingFields = Object.keys(validationErrors).map(
        (field) => stepFieldLabels[field] || field,
      );
      showAlert(
        `Please fill in all required fields:\n${missingFields.join(', ')}`,
        'Required Fields',
      );

      const fieldOrder = [
        'addressProofType',
        'addressProofNumber',
        'idProofNumber',
        'terms',
      ];
      const firstErrorField = fieldOrder.find(
        (field) => validationErrors[field],
      );
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }, 100);
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      return;
    }

    if (!form.terms) return;

    // Designation Business Validation (Final Check)
    if (canShowOldBusiness) {
      const isOldBusinessDisabled =
        !!editingAssociate && form.totalBookedAmount > 0;

      if (!isOldBusinessDisabled) {
        const selectedDesignationObj = designations.find(
          (d) => d.value === form.designation,
        );
        if (
          selectedDesignationObj &&
          (selectedDesignationObj.fromAmount || 0) > 0
        ) {
          const minRequiredBusiness = selectedDesignationObj.fromAmount;
          const currentOldBusiness = parseFloat(form.oldBusiness) || 0;

          if (currentOldBusiness < minRequiredBusiness) {
            // Auto-fill the required amount in the background if it's missing or too low
            form.oldBusiness = String(minRequiredBusiness);
          }
        }
      }
    }

    if (!validateMobile(form.mobile)) {
      setMobileError('Mobile number must be 10 digits and start with 6-9');
      return;
    }

    if (form.whatsapp && !validateMobile(form.whatsapp)) {
      setWhatsappError('WhatsApp number must be 10 digits and start with 6-9');
      return;
    }

    if (form.altMobile && !validateMobile(form.altMobile)) {
      setAltMobileError(
        'Alternate mobile number must be 10 digits and start with 6-9',
      );
      return;
    }

    if (form.email && !validateEmail(form.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      setError('');
      const payload = {
        ...buildPayload(form),
        ...(editingAssociate && editingAssociate._id
          ? { _id: editingAssociate._id }
          : {}),
      };
      let success = false;
      let message = '';

      let associateId = null;
      let associateResponse = null;

      if (editingAssociate && editingAssociate._id) {
        associateId = editingAssociate._id;
        const body = await updateAssociate(associateId, payload);
        if (body?.status === false || body?.success === false) {
          const err = new Error(body?.message || 'Failed to update associate');
          if (Array.isArray(body?.errors)) err.errors = body.errors;
          throw err;
        }
        associateResponse = body;
        success = true;
        message = body?.message || 'Associate updated successfully';
      } else {
        // CREATE FLOW: Create new associate first
        const response = await createAssociate(payload);
        if (!response?.status) {
          throw new Error(response?.message || 'Failed to create associate');
        }
        associateResponse = response;
        // Get associate ID from response
        associateId =
          response?.data?._id || response?.data?.id || response?._id || null;
        if (!associateId) {
          throw new Error('Associate created but ID not found in response');
        }
        success = true;
        message = response?.message || 'Associate created successfully';
      }

      // Handle file uploads if associate was created/updated successfully and files are present
      // This runs AFTER successful create/update
      if (success && associateId) {
        const filesToUpload = [];
        const fileMap = {}; // Map to track which file goes to which field

        // Helper function to get file extension
        const getFileExtension = (fileName) => {
          const parts = fileName.split('.');
          return parts.length > 1
            ? parts[parts.length - 1].toLowerCase()
            : 'jpg';
        };

        // Collect all files that need to be uploaded
        if (form.profileImage) {
          const ext = getFileExtension(form.profileImage.name);
          const fileName = `${associateId}-profile.${ext}`;
          filesToUpload.push({
            category: 'associate',
            fileName: fileName,
            fileType: form.profileImage.type || 'image/jpeg',
            file: form.profileImage,
            field: 'img',
          });
        }

        if (form.idProofFile) {
          const ext = getFileExtension(form.idProofFile.name);
          const fileName = `${associateId}-pan.${ext}`;
          filesToUpload.push({
            category: 'associate',
            fileName: fileName,
            fileType: form.idProofFile.type || 'image/jpeg',
            file: form.idProofFile,
            field: 'panImage',
          });
        }

        if (form.addressImage) {
          const ext = getFileExtension(form.addressImage.name);
          const fileName = `${associateId}-adhaar.${ext}`;
          filesToUpload.push({
            category: 'associate',
            fileName: fileName,
            fileType: form.addressImage.type || 'image/jpeg',
            file: form.addressImage,
            field: 'addressImage',
          });
        }

        if (form.drivingLicenceImage) {
          const ext = getFileExtension(form.drivingLicenceImage.name);
          const fileName = `${associateId}-driving-license.${ext}`;
          filesToUpload.push({
            category: 'associate',
            fileName: fileName,
            fileType: form.drivingLicenceImage.type || 'image/jpeg',
            file: form.drivingLicenceImage,
            field: 'drivingLicenseImage',
          });
        }

        if (form.voterIdImage) {
          const ext = getFileExtension(form.voterIdImage.name);
          const fileName = `${associateId}-voter-id.${ext}`;
          filesToUpload.push({
            category: 'associate',
            fileName: fileName,
            fileType: form.voterIdImage.type || 'image/jpeg',
            file: form.voterIdImage,
            field: 'voterIdImage',
          });
        }

        if (form.bankDocFile) {
          const ext = getFileExtension(form.bankDocFile.name);
          const fileName = `${associateId}-bank-doc.${ext}`;
          filesToUpload.push({
            category: 'associate',
            fileName: fileName,
            fileType: form.bankDocFile.type || 'application/pdf',
            file: form.bankDocFile,
            field: 'bankDocFile',
          });
        }

        // Upload files if any
        if (filesToUpload.length > 0) {
          try {
            // Prepare files array for signed URL API
            const filesForSignedUrl = filesToUpload.map((f) => ({
              category: f.category,
              fileName: f.fileName,
              fileType: f.fileType,
            }));

            // Get signed URLs
            const signedUrls = await getSignedUrls(filesForSignedUrl);

            // Upload files to S3 and collect URLs
            // Create a map of fileName to signedUrlInfo for easier lookup
            const signedUrlMap = {};
            signedUrls.forEach((signedUrlInfo) => {
              if (signedUrlInfo && signedUrlInfo.fileName) {
                signedUrlMap[signedUrlInfo.fileName] = signedUrlInfo;
              }
            });

            // Get existing personalDetail data to preserve it
            // For CREATE: Use payload personalDetail (the data we just sent)
            // For EDIT: Use editingAssociate personalDetail or response personalDetail
            let existingPersonalDetail = {};
            if (editingAssociate && editingAssociate._id) {
              // EDIT FLOW: Get from editingAssociate or response
              if (
                editingAssociate.personalDetail &&
                Array.isArray(editingAssociate.personalDetail) &&
                editingAssociate.personalDetail[0]
              ) {
                existingPersonalDetail = {
                  ...editingAssociate.personalDetail[0],
                };
              } else if (
                associateResponse?.data?.personalDetail &&
                Array.isArray(associateResponse.data.personalDetail) &&
                associateResponse.data.personalDetail[0]
              ) {
                existingPersonalDetail = {
                  ...associateResponse.data.personalDetail[0],
                };
              }
            } else {
              // CREATE FLOW: Use the personalDetail from the payload we just sent
              const payloadPersonalDetail = payload?.personalDetail;
              if (
                payloadPersonalDetail &&
                Array.isArray(payloadPersonalDetail) &&
                payloadPersonalDetail[0]
              ) {
                existingPersonalDetail = { ...payloadPersonalDetail[0] };
              } else if (
                associateResponse?.data?.personalDetail &&
                Array.isArray(associateResponse.data.personalDetail) &&
                associateResponse.data.personalDetail[0]
              ) {
                // Fallback: get from response if available
                existingPersonalDetail = {
                  ...associateResponse.data.personalDetail[0],
                };
              }
            }

            const fileUrls = {};
            for (const fileInfo of filesToUpload) {
              const signedUrlInfo = signedUrlMap[fileInfo.fileName];

              if (signedUrlInfo && signedUrlInfo.uploadUrl) {
                // Upload file to S3
                const uploadSuccess = await uploadFileToS3(
                  signedUrlInfo.uploadUrl,
                  fileInfo.file,
                  fileInfo.fileType,
                );

                // Only proceed if upload was successful
                if (uploadSuccess) {
                  // Extract the full S3 URL from the signed URL (remove query parameters)
                  // The uploadUrl format: https://bucket.s3.region.amazonaws.com/path/file.jpg?query=params
                  // After split('?')[0], we get the full URL: https://bucket.s3.region.amazonaws.com/path/file.jpg
                  const s3Url = signedUrlInfo.uploadUrl.split('?')[0];

                  // Verify we have a full URL (must start with http:// or https://)
                  if (
                    !s3Url ||
                    (!s3Url.startsWith('http://') &&
                      !s3Url.startsWith('https://'))
                  ) {
                    console.error('Invalid S3 URL extracted:', s3Url);
                    throw new Error(
                      `Failed to extract valid S3 URL for file: ${fileInfo.fileName}`,
                    );
                  }

                  // Map to the correct field based on field type
                  if (
                    fileInfo.field === 'panImage' ||
                    fileInfo.field === 'addressImage'
                  ) {
                    // These go in personalDetail array - merge with existing data
                    if (!fileUrls.personalDetail) {
                      fileUrls.personalDetail = [{ ...existingPersonalDetail }];
                    }
                    fileUrls.personalDetail[0][fileInfo.field] = s3Url;
                  } else if (fileInfo.field === 'img') {
                    // Profile image goes at root level
                    fileUrls.img = s3Url;
                  } else {
                    // For other files, store at root level for now
                    // You may need to adjust based on your API structure
                    fileUrls[fileInfo.field] = s3Url;
                  }
                }
              }
            }

            // Handle deleted existing images - set them to empty string to clear from database
            if (!existingImageUrls.profileImageUrl && editingAssociate?.img) {
              // Profile image was deleted
              fileUrls.img = '';
            }
            if (
              !existingImageUrls.addressImageUrl &&
              editingAssociate?.personalDetail?.[0]?.addressImage
            ) {
              // Address image was deleted
              if (!fileUrls.personalDetail) {
                fileUrls.personalDetail = [{ ...existingPersonalDetail }];
              }
              fileUrls.personalDetail[0].addressImage = '';
            }
            if (
              !existingImageUrls.panImageUrl &&
              editingAssociate?.personalDetail?.[0]?.panImage
            ) {
              // PAN image was deleted
              if (!fileUrls.personalDetail) {
                fileUrls.personalDetail = [{ ...existingPersonalDetail }];
              }
              fileUrls.personalDetail[0].panImage = '';
            }

            // Ensure personalDetail is included even if no file URLs were added to it
            // (to preserve existing data)
            if (
              (fileUrls.personalDetail &&
                fileUrls.personalDetail[0] &&
                Object.keys(fileUrls.personalDetail[0]).length > 0) ||
              Object.keys(existingPersonalDetail).length > 0
            ) {
              if (!fileUrls.personalDetail) {
                fileUrls.personalDetail = [{ ...existingPersonalDetail }];
              } else {
                // Merge any remaining existing fields that weren't updated
                fileUrls.personalDetail[0] = {
                  ...existingPersonalDetail,
                  ...fileUrls.personalDetail[0],
                };
              }
            }

            // Update associate with file URLs
            if (Object.keys(fileUrls).length > 0) {
              await updateAssociateFiles(associateId, fileUrls);
            }
          } catch (uploadError) {
            console.error('File upload error:', uploadError);
            showAlert(
              'Associate saved but file upload failed: ' + uploadError.message,
              'Warning',
            );
          }
        }

        // Handle deleted existing images (even if no new files were uploaded)
        // Check if existing images were deleted and need to be cleared from database
        if (editingAssociate && editingAssociate._id) {
          const deletedImageUrls = {};
          let hasDeletedImages = false;

          // Check if profile image was deleted
          if (!existingImageUrls.profileImageUrl && editingAssociate.img) {
            deletedImageUrls.img = '';
            hasDeletedImages = true;
          }

          // Check if address or PAN images were deleted
          const personal =
            Array.isArray(editingAssociate.personalDetail) &&
            editingAssociate.personalDetail[0]
              ? editingAssociate.personalDetail[0]
              : {};
          if (!existingImageUrls.addressImageUrl && personal.addressImage) {
            if (!deletedImageUrls.personalDetail) {
              deletedImageUrls.personalDetail = [{ ...personal }];
            }
            deletedImageUrls.personalDetail[0].addressImage = '';
            hasDeletedImages = true;
          }
          if (!existingImageUrls.panImageUrl && personal.panImage) {
            if (!deletedImageUrls.personalDetail) {
              deletedImageUrls.personalDetail = [{ ...personal }];
            }
            deletedImageUrls.personalDetail[0].panImage = '';
            hasDeletedImages = true;
          }

          // Check if bank document was deleted
          const existingBankDocSource =
            editingAssociate?.bankDetail?.[0]?.bankDocFileUrl ||
            editingAssociate?.bankDetail?.[0]?.bankDocFile ||
            editingAssociate?.bankDocFile ||
            '';

          if (!existingImageUrls.bankDocFileUrl && existingBankDocSource) {
            deletedImageUrls.bankDocFile = '';
            hasDeletedImages = true;
          }

          // Check if Driving License was deleted
          if (
            !existingImageUrls.drivingLicenseImageUrl &&
            editingAssociate.drivingLicenseImage
          ) {
            deletedImageUrls.drivingLicenseImage = '';
            hasDeletedImages = true;
          }

          // Check if Voter ID was deleted
          if (
            !existingImageUrls.voterIdImageUrl &&
            editingAssociate.voterIdImage
          ) {
            deletedImageUrls.voterIdImage = '';
            hasDeletedImages = true;
          }

          // Update associate to clear deleted images
          if (hasDeletedImages) {
            try {
              await updateAssociateFiles(associateId, deletedImageUrls);
            } catch (deleteError) {
              console.error('Failed to delete images:', deleteError);
              // Don't fail the entire operation
            }
          }
        }
      }

      if (success) {
        showAlert(message, 'Success');
        setTimeout(() => {
          history.push('/app/users/AssociateList');
        }, 1500);
      }
    } catch (err) {
      const topMsg = err?.message || 'Registration failed';
      setError(topMsg);
      showAlert(topMsg, 'Error');
      if (Array.isArray(err?.errors)) {
        err.errors.forEach((e) => {
          const m = e?.msg || e?.message;
          if (m) showAlert(m, 'Error');
        });
      }
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (!canWriteAssociate) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: 'center',
            maxWidth: 500,
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          }}
        >
          <Avatar
            sx={{
              bgcolor: '#fee2e2',
              color: '#ef4444',
              width: 80,
              height: 80,
              mb: 3,
              mx: 'auto',
            }}
          >
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography
            variant='h4'
            fontWeight='bold'
            color='#1a202c'
            gutterBottom
          >
            Access Denied
          </Typography>
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ mb: 4, fontSize: '1.1rem' }}
          >
            You do not have permission to register or edit associates. Please
            contact your administrator for access.
          </Typography>
          <Button
            variant='contained'
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4,
              py: 1.2,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: '#fff', minHeight: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant='h4'
          sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}
        >
          Associate Registration
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Register a new associate with complete details
        </Typography>
      </Box>
      <Card
        sx={{
          mb: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 2,
          backgroundColor: '#ffffff',
        }}
      >
        <CardContent>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        {activeStep === 0 && (
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              mb: 2,
              backgroundColor: '#ffffff',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display='flex' alignItems='center' gap={1} mb={2}>
                <PersonIcon sx={{ color: '#536DFE' }} />
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: '#000' }}
                >
                  Personal Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px dashed #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{ mb: 2, fontWeight: 600, color: '#424242' }}
                    >
                      Profile Picture
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        flexWrap: 'wrap',
                      }}
                    >
                      {form.profileImage ? (
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={URL.createObjectURL(form.profileImage)}
                            alt='Profile'
                            sx={{
                              width: 120,
                              height: 120,
                              border: '3px solid #536DFE',
                              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                            }}
                          />
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleRemoveFile('profileImage')}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              '&:hover': { bgcolor: '#ffebee' },
                            }}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      ) : existingImageUrls.profileImageUrl ? (
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={existingImageUrls.profileImageUrl}
                            alt='Profile'
                            onClick={() =>
                              handleOpenImagePreview(
                                existingImageUrls.profileImageUrl,
                                'Profile Picture',
                              )
                            }
                            sx={{
                              width: 120,
                              height: 120,
                              border: '3px solid #2e7d32',
                              boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                                transform: 'scale(1.05)',
                                transition: 'all 0.2s ease-in-out',
                              },
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              display: 'flex',
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size='small'
                              color='primary'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadExistingImage(
                                  existingImageUrls.profileImageUrl,
                                  'Profile Picture',
                                );
                              }}
                              sx={{
                                bgcolor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                '&:hover': { bgcolor: '#e3f2fd' },
                              }}
                              title='Download image'
                            >
                              <DownloadIcon fontSize='small' />
                            </IconButton>
                            <IconButton
                              size='small'
                              color='error'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveExistingImage('profileImage');
                              }}
                              sx={{
                                bgcolor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                '&:hover': { bgcolor: '#ffebee' },
                              }}
                              title='Delete image'
                            >
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </Box>
                          <Chip
                            icon={<CheckCircleIcon />}
                            label='Existing'
                            size='small'
                            color='success'
                            sx={{
                              position: 'absolute',
                              top: -8,
                              left: -8,
                            }}
                          />
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            onClick={() =>
                              handleOpenImagePreview(
                                existingImageUrls.profileImageUrl,
                                'Profile Picture',
                              )
                            }
                            sx={{
                              position: 'absolute',
                              bottom: -20,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#536DFE',
                              },
                            }}
                          >
                            Click to view
                          </Typography>
                        </Box>
                      ) : (
                        <Avatar
                          sx={{
                            width: 120,
                            height: 120,
                            bgcolor: '#e3f2fd',
                            border: '2px dashed #536DFE',
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 60, color: '#536DFE' }} />
                        </Avatar>
                      )}
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Button
                          variant='outlined'
                          component='label'
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            borderColor: '#536DFE',
                            color: '#536DFE',
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                              borderColor: '#536DFE',
                              backgroundColor: '#f3f4ff',
                            },
                          }}
                        >
                          {form.profileImage
                            ? 'Change Image'
                            : 'Upload Profile Picture'}
                          <input
                            type='file'
                            hidden
                            accept='image/*'
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (!file.type.startsWith('image/')) {
                                  setError(`Please select a valid image file.`);
                                  return;
                                }
                                handleFileChange('profileImage', file);
                              }
                            }}
                          />
                        </Button>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ mt: 1, display: 'block' }}
                        >
                          Recommended: Square image, Max 2MB (JPG, PNG, etc.)
                        </Typography>
                        {form.profileImage && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {form.profileImage.name} (
                            {formatFileSize(form.profileImage.size)})
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3} id='field-sponsorCode'>
                  <Autocomplete
                    fullWidth
                    disabled={!!editingAssociate}
                    options={sponsorDirectory}
                    getOptionLabel={(opt) =>
                      opt
                        ? `${opt.name}${opt.mobile ? ` — ${opt.mobile}` : ''}`
                        : ''
                    }
                    loading={loadingSponsors}
                    inputValue={sponsorSearchInput}
                    onInputChange={(event, newInputValue, reason) => {
                      if (event && event.type === 'blur') {
                        return;
                      }
                      setSponsorSearchInput(newInputValue);
                    }}
                    onFocus={() => {
                      if (sponsorDirectory.length === 0 && !loadingSponsors) {
                        searchSponsors('');
                      }
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      const optCode = String(option.code || option.id);
                      const valCode = String(value.code || value.id);
                      return optCode === valCode;
                    }}
                    value={
                      form.sponsorCode || form.sponsorId
                        ? {
                            id: form.sponsorId,
                            code: form.sponsorCode || form.sponsorId,
                            name: form.sponsorName,
                            mobile: form.sponsorMobile,
                            designation: form.sponsorDesignation,
                            commission: form.sponsorCommission,
                          }
                        : null
                    }
                    onChange={(_, v) => {
                      if (v) {
                        handleChange('sponsorCode', v.code);
                        handleChange('sponsorName', v.name);
                        handleChange('sponsorId', v.id);
                        handleChange('sponsorMobile', v.mobile || '');
                        handleChange('sponsorDesignation', v.designation || '');
                        handleChange('sponsorCommission', v.commission || '');
                        setSponsorSearchInput(
                          v
                            ? `${v.name}${v.mobile ? ` — ${v.mobile}` : ''}`
                            : '',
                        );
                        // Reset designation when sponsor changes to enforce hierarchy
                        handleChange('designation', '');
                        handleChange('commission', '');
                      } else {
                        handleChange('sponsorCode', '');
                        handleChange('sponsorName', '');
                        handleChange('sponsorId', '');
                        handleChange('sponsorMobile', '');
                        handleChange('sponsorDesignation', '');
                        handleChange('sponsorCommission', '');
                        handleChange('sponsorPermissionCount', null); // NEW: Clear permission count
                        handleChange('designation', '');
                        handleChange('commission', '');
                        setSponsorSearchInput('');
                      }
                      setStep0Errors((prev) => ({ ...prev, sponsorCode: '' }));
                    }}
                    onBlur={() => handleFieldBlurStep0('sponsorCode')}
                    filterOptions={(options) => options}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Sponsor (ID / Name / Mobile)'
                        required
                        error={!!step0Errors.sponsorCode}
                        helperText={step0Errors.sponsorCode}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>

                {form.sponsorCode && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label='Sponsor Mobile'
                        value={form.sponsorMobile}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position='start'>
                              <PhoneIcon color='action' />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f5f5f5',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label='Sponsor Designation'
                        value={form.sponsorDesignation}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position='start'>
                              <WorkIcon color='action' />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f5f5f5',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label='Commission (%)'
                        value={form.sponsorCommission}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position='end'>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                %
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f5f5f5',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6} md={3} id='field-registrationDate'>
                  <TextField
                    label='Registration Date'
                    type='date'
                    fullWidth
                    required
                    value={form.registrationDate}
                    onChange={(e) =>
                      handleChange('registrationDate', e.target.value)
                    }
                    onBlur={() => handleFieldBlurStep0('registrationDate')}
                    error={!!step0Errors.registrationDate}
                    helperText={step0Errors.registrationDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} id='field-name'>
                  <TextField
                    label='Full Name'
                    fullWidth
                    required
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={() => handleFieldBlurStep0('name')}
                    error={!!step0Errors.name}
                    helperText={step0Errors.name}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Father's Name"
                    fullWidth
                    value={form.fatherName}
                    onChange={(e) => handleChange('fatherName', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Mother's Name"
                    fullWidth
                    value={form.motherName}
                    onChange={(e) => handleChange('motherName', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-gender'>
                  <TextField
                    label='Gender'
                    select
                    fullWidth
                    required
                    value={form.gender}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    onBlur={() => handleFieldBlurStep0('gender')}
                    error={!!step0Errors.gender}
                    helperText={step0Errors.gender}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value='Male'>Male</MenuItem>
                    <MenuItem value='Female'>Female</MenuItem>
                    <MenuItem value='Other'>Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-marriedStatus'>
                  <TextField
                    label='Marital Status'
                    select
                    fullWidth
                    required
                    value={form.marriedStatus}
                    onChange={(e) =>
                      handleChange('marriedStatus', e.target.value)
                    }
                    onBlur={() => handleFieldBlurStep0('marriedStatus')}
                    error={!!step0Errors.marriedStatus}
                    helperText={step0Errors.marriedStatus}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value='Single'>Single</MenuItem>
                    <MenuItem value='Married'>Married</MenuItem>
                  </TextField>
                </Grid>
                {form.marriedStatus === 'Married' && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label='Anniversary Date'
                      type='date'
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={toDateOnly(form.anniversary)}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0); // Reset time to start of day

                        if (selectedDate > today) {
                          // If future date selected, prevent the change
                          return;
                        }
                        handleChange('anniversary', e.target.value);
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      inputProps={{
                        max: new Date().toISOString().split('T')[0], // Restrict to current date or earlier
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3} id='field-dob'>
                  <TextField
                    label='Date of Birth'
                    type='date'
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={toDateOnly(form.dob)}
                    onChange={(e) => handleChange('dob', e.target.value)}
                    onBlur={() => handleFieldBlurStep0('dob')}
                    error={!!step0Errors.dob}
                    helperText={step0Errors.dob}
                    inputProps={{
                      max: (() => {
                        const today = new Date();
                        const tenYearsAgo = new Date(
                          today.getFullYear() - 10,
                          today.getMonth(),
                          today.getDate(),
                        );
                        return tenYearsAgo.toISOString().split('T')[0];
                      })(),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-designation'>
                  <TextField
                    label='Designation'
                    select
                    fullWidth
                    required
                    value={form.designation}
                    disabled={!!editingAssociate && form.totalBookedAmount > 0}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const selectedDesig = designations.find(
                        (d) => d.value === selectedName,
                      );

                      // Always update commission based on designation (or clear it)
                      const commissionVal = selectedDesig?.commission || '';

                      setForm((prev) => {
                        const minBusiness = selectedDesig?.fromAmount;
                        return {
                          ...prev,
                          designation: selectedName,
                          commission: commissionVal,
                          // Auto-fill Old Business if it has a required minimum amount
                          oldBusiness:
                            minBusiness || minBusiness === 0
                              ? String(minBusiness)
                              : prev.oldBusiness,
                        };
                      });

                      clearFieldError('designation');
                      if (commissionVal) clearFieldError('commission');
                    }}
                    onBlur={() => handleFieldBlurStep0('designation')}
                    error={!!step0Errors.designation}
                    helperText={step0Errors.designation}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {designations
                      .filter((designation) => {
                        // ALWAYS show the current designation in Edit mode to prevent empty/invisible selection
                        if (
                          editingAssociate &&
                          designation.value === form.designation
                        ) {
                          return true;
                        }

                        // Dynamic filter based on Sponsor Level
                        const sponsorDesigName = (
                          form.sponsorDesignation || ''
                        ).toLowerCase();

                        // Condition: Owner can add any level of designation
                        if (sponsorDesigName.includes('owner')) {
                          return true;
                        }

                        const foundSponsor = designations.find(
                          (d) => d.value === form.sponsorDesignation,
                        );
                        const sponsorLevel = foundSponsor
                          ? Number(foundSponsor.level)
                          : 0;
                        const targetLevel = Number(designation.level);

                        // Allow same level or lower (User request: "login same level wise")
                        return targetLevel <= sponsorLevel;
                      })
                      .map((designation) => (
                        <MenuItem
                          key={designation.id}
                          value={designation.value}
                        >
                          {designation.name} ({designation.commission}%)
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-commission'>
                  <TextField
                    label='Commission (%)'
                    fullWidth
                    value={form.commission}
                    InputProps={{
                      readOnly: true,
                    }}
                    error={!!step0Errors.commission}
                    helperText={
                      step0Errors.commission ||
                      'Auto-filled based on Designation'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-role'>
                  <TextField
                    label='Role'
                    select
                    fullWidth
                    required
                    value={form.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    onBlur={() => handleFieldBlurStep0('role')}
                    error={!!step0Errors.role}
                    helperText={step0Errors.role}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {roles
                      .filter((role) => {
                        // Check if the logged-in user is an Admin
                        const isLoggedUserAdmin =
                          loggedInUserRole.includes('owner') ||
                          loggedInUserRole.includes('administrator') ||
                          loggedInUserRole.includes('admin');

                        // If the logged-in user is an Admin, they can see and select ALL roles
                        if (isLoggedUserAdmin) return true;

                        const isRoleAdmin =
                          role.value?.toLowerCase() === 'admin' ||
                          role.name?.toLowerCase() === 'admin' ||
                          role.roleType?.toLowerCase() === 'admin';

                        // If the logged-in user is NOT an Admin, they should NEVER see or select the Admin role
                        if (isRoleAdmin) {
                          return false;
                        }

                        // If this is the currently assigned/selected role for the associate, do not hide it
                        if (
                          form.role &&
                          role.value?.toLowerCase() === form.role.toLowerCase()
                        )
                          return true;

                        // Otherwise (for regular users / associates):
                        // 1. Do not show "owner" or "administrator" roles
                        const isSuperAdminRole =
                          role.value?.toLowerCase().includes('owner') ||
                          role.value?.toLowerCase().includes('administrator');
                        if (isSuperAdminRole) return false;

                        // 2. Apply Sponsor Hierarchy Filtering
                        if (form.sponsorPermissionCount === null) return true;

                        // Otherwise, only show roles with less THAN OR EQUAL permission count
                        return (
                          (role.permissionCount || 0) <=
                          (form.sponsorPermissionCount || 0)
                        );
                      })
                      .map((role) => (
                        <MenuItem key={role.id} value={role.value}>
                          {role.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                {/* Old Business field is hidden but handled in background logic */}
              </Grid>

              <Grid item xs={12}>
                <Divider textAlign='left' sx={{ mb: 2, mt: 2 }}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Nominee Details
                  </Typography>
                </Divider>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label='Nominee Name'
                    fullWidth
                    value={form.nomineeName}
                    onChange={(e) =>
                      handleChange('nomineeName', e.target.value)
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label='Nominee Mobile Number'
                    fullWidth
                    value={form.nomineePhone}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 10);
                      handleChange('nomineePhone', value);
                    }}
                    onBlur={(e) => {
                      if (
                        form.nomineePhone &&
                        !validateMobile(form.nomineePhone)
                      ) {
                        setNomineePhoneError(
                          'Nominee mobile number must be 10 digits and start with 6-9',
                        );
                      } else {
                        setNomineePhoneError('');
                      }
                    }}
                    error={!!nomineePhoneError}
                    helperText={nomineePhoneError || ''}
                    inputProps={{
                      maxLength: 10,
                      inputMode: 'numeric',
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      '& input[type=text]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=text]::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label='Relation with Nominee'
                    select
                    fullWidth
                    value={form.nomineeRelation}
                    onChange={(e) =>
                      handleChange('nomineeRelation', e.target.value)
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value='father'>Father</MenuItem>
                    <MenuItem value='mother'>Mother</MenuItem>
                    <MenuItem value='wife'>Wife</MenuItem>
                    <MenuItem value='husband'>Husband</MenuItem>
                    <MenuItem value='son'>Son</MenuItem>
                    <MenuItem value='daughter'>Daughter</MenuItem>
                    <MenuItem value='brother'>Brother</MenuItem>
                    <MenuItem value='friend'>Friend</MenuItem>
                    <MenuItem value='self'>Self</MenuItem>
                    <MenuItem value='other'>Other</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Box display='flex' justifyContent='flex-end' gap={2} mt={3}>
                <Button
                  variant='outlined'
                  onClick={() => history.push('/app/users/AssociateList')}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleNext}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 4,
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  }}
                >
                  Next
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
        {activeStep === 1 && (
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              mb: 3,
              backgroundColor: '#ffffff',
            }}
          >
            <CardContent>
              <Box display='flex' alignItems='center' gap={1} mb={3}>
                <BusinessIcon sx={{ color: '#536DFE' }} />
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: '#000' }}
                >
                  Contact & Address Details
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3} id='field-mobile'>
                  <TextField
                    label='Mobile Number'
                    type='text'
                    fullWidth
                    required
                    value={form.mobile}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    onBlur={() => handleFieldBlurStep1('mobile')}
                    error={!!step1Errors.mobile || !!mobileError}
                    helperText={
                      step1Errors.mobile ||
                      mobileError ||
                      (form.mobile && form.mobile.length === 10
                        ? ''
                        : 'Enter 10 digit mobile number')
                    }
                    inputProps={{
                      maxLength: 10,
                      inputMode: 'numeric',
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='WhatsApp Number'
                    type='text'
                    fullWidth
                    value={form.whatsapp}
                    onChange={(e) => handleWhatsappChange(e.target.value)}
                    error={!!whatsappError}
                    helperText={
                      whatsappError ||
                      (form.whatsapp && form.whatsapp.length === 10
                        ? ''
                        : 'Enter 10 digit WhatsApp number (optional)')
                    }
                    inputProps={{
                      maxLength: 10,
                      inputMode: 'numeric',
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Alternate Mobile Number'
                    type='text'
                    fullWidth
                    value={form.altMobile}
                    onChange={(e) => handleAltMobileChange(e.target.value)}
                    error={!!altMobileError || !!step1Errors.altMobile}
                    helperText={
                      altMobileError ||
                      step1Errors.altMobile ||
                      (form.altMobile && form.altMobile.length === 10
                        ? ''
                        : 'Enter 10 digit number (optional)')
                    }
                    inputProps={{ maxLength: 10 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-email'>
                  <TextField
                    label='Email ID'
                    type='email'
                    fullWidth
                    required
                    value={form.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onBlur={() => handleFieldBlurStep1('email')}
                    error={!!step1Errors.email || !!emailError}
                    helperText={
                      step1Errors.email ||
                      emailError ||
                      (form.email && validateEmail(form.email)
                        ? ''
                        : 'Enter valid email address')
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Login ID'
                    fullWidth
                    value={generatedLoginId}
                    disabled
                    helperText='Auto-generated from name'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Password'
                    type='text'
                    fullWidth
                    value={generatedPassword}
                    disabled
                    helperText='Auto-generated from name'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='State'
                    select
                    fullWidth
                    value={form.state}
                    onChange={(e) => {
                      handleChange('state', e.target.value);
                      handleChange('city', '');
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {states.map((state) => (
                      <MenuItem key={state.id} value={state.value}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='City'
                    select
                    fullWidth
                    value={form.city}
                    onChange={(e) => {
                      if (e.target.value === '__add_city__') {
                        setAddCityDialogOpen(true);
                        setNewCityName('');
                      } else {
                        handleChange('city', e.target.value);
                      }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    disabled={!form.state}
                    helperText={
                      !form.state ? 'Please select a state first' : ''
                    }
                  >
                    <MenuItem
                      value='__add_city__'
                      sx={{ color: '#1976d2', fontWeight: 500 }}
                    >
                      ➕ Add New City
                    </MenuItem>
                    {filteredCities.map((city) => (
                      <MenuItem key={city.id} value={city.value}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Pincode'
                    type='text'
                    fullWidth
                    value={form.pinCode}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 6);
                      handleChange('pinCode', value);
                      setPincodeError(validatePincode(value));
                    }}
                    onBlur={(e) => {
                      setPincodeError(validatePincode(form.pinCode));
                    }}
                    error={!!pincodeError}
                    helperText={pincodeError || 'Enter 6-digit pincode'}
                    // InputProps={{
                    //   readOnly: !!editingAssociate
                    // }}
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      '& input[type=text]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=text]::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=text]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='Address'
                    fullWidth
                    multiline
                    rows={3}
                    value={form.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>

              <Box display='flex' justifyContent='space-between' gap={2} mt={4}>
                <Button
                  variant='outlined'
                  onClick={handleBack}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                >
                  Back
                </Button>
                <Button
                  variant='contained'
                  onClick={handleNext}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 4,
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  }}
                >
                  Next
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
        {activeStep === 2 && (
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              mb: 3,
              backgroundColor: '#ffffff',
            }}
          >
            <CardContent>
              <Box display='flex' alignItems='center' gap={1} mb={3}>
                <DocumentIcon sx={{ color: '#536DFE' }} />
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: '#000' }}
                >
                  KYC Documents
                </Typography>
              </Box>

              <Alert severity='info' sx={{ mb: 3, borderRadius: 2 }}>
                Please upload clear scanned copies or photos of the documents
                (Max 5MB each)
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Divider textAlign='left' sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Address Proof
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-addressProofType'>
                  <TextField
                    label='Address Proof Type'
                    select
                    fullWidth
                    required
                    value={form.addressProofType}
                    onChange={(e) =>
                      handleAddressProofChange(
                        'addressProofType',
                        e.target.value,
                      )
                    }
                    onBlur={() => handleFieldBlurStep2('addressProofType')}
                    error={!!step2Errors.addressProofType}
                    helperText={step2Errors.addressProofType}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value='Aadhar'>Aadhar Card</MenuItem>
                    <MenuItem value='DL'>Driving License</MenuItem>
                    <MenuItem value='VoterId'>Voter ID</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-addressProofNumber'>
                  <TextField
                    label={`${form.addressProofType || 'Address Proof'} Number`}
                    fullWidth
                    value={form.addressProofNumber}
                    onChange={(e) =>
                      handleAddressProofChange(
                        'addressProofNumber',
                        e.target.value,
                      )
                    }
                    onBlur={() => handleFieldBlurStep2('addressProofNumber')}
                    error={!!addressProofError}
                    helperText={
                      addressProofError ||
                      getAddressProofHelperText(form.addressProofType)
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    inputProps={{
                      style:
                        form.addressProofType === 'Aadhar' ||
                        form.addressProofType === 'DL' ||
                        form.addressProofType === 'VoterId'
                          ? { textTransform: 'uppercase' }
                          : {},
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {form.addressProofFile ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}
                        >
                          {getFileIcon(form.addressProofFile)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {form.addressProofFile.name}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatFileSize(form.addressProofFile.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => handleRemoveFile('addressProofFile')}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label='Uploaded'
                        size='small'
                        color='success'
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  ) : existingImageUrls.addressImageUrl ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}
                        >
                          <ImageIcon />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Existing {form.addressProofType || 'Address'} Image
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            onClick={() =>
                              handleOpenImagePreview(
                                existingImageUrls.addressImageUrl,
                                `Existing ${form.addressProofType || 'Address'} Image`,
                              )
                            }
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#536DFE',
                              },
                            }}
                          >
                            Click to view
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={() =>
                              handleDownloadExistingImage(
                                existingImageUrls.addressImageUrl,
                                `Existing ${form.addressProofType || 'Address'} Image`,
                              )
                            }
                            title='Download image'
                          >
                            <DownloadIcon fontSize='small' />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() =>
                              handleRemoveExistingImage('addressImage')
                            }
                            title='Delete image'
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <img
                          src={existingImageUrls.addressImageUrl}
                          alt='Address Proof'
                          style={{
                            width: '100%',
                            maxHeight: '150px',
                            objectFit: 'contain',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            console.log(
                              'Opening preview for address image:',
                              existingImageUrls.addressImageUrl,
                            );
                            handleOpenImagePreview(
                              existingImageUrls.addressImageUrl,
                              `Existing ${form.addressProofType || 'Address'} Image`,
                            );
                          }}
                          onError={(e) => {
                            console.error(
                              'Failed to load address image thumbnail:',
                              existingImageUrls.addressImageUrl,
                            );
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label='Existing'
                        size='small'
                        color='success'
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  ) : (
                    <Button
                      variant='outlined'
                      component='label'
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        height: 120,
                        borderRadius: 2,
                        textTransform: 'none',
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        borderColor: '#536DFE',
                        color: '#536DFE',
                        flexDirection: 'column',
                        gap: 1,
                        '&:hover': {
                          borderColor: '#536DFE',
                          backgroundColor: '#f3f4ff',
                        },
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 32 }} />
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Upload Document
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Max 5MB (Image/PDF)
                      </Typography>
                      <input
                        type='file'
                        hidden
                        accept='image/*,.pdf'
                        onChange={(e) =>
                          handleFileChange(
                            'addressProofFile',
                            e.target.files[0],
                          )
                        }
                      />
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider textAlign='left' sx={{ mb: 2, mt: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      PAN Card
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={6} md={3} id='field-idProofNumber'>
                  <TextField
                    label='PAN Card Number'
                    fullWidth
                    value={form.idProofNumber}
                    onChange={(e) => {
                      const upperValue = e.target.value.toUpperCase();
                      handleChange('idProofNumber', upperValue);
                      if (step2Errors.idProofNumber) {
                        clearFieldError('idProofNumber');
                      }
                    }}
                    onBlur={() => handleFieldBlurStep2('idProofNumber')}
                    error={!!step2Errors.idProofNumber}
                    helperText={
                      step2Errors.idProofNumber ||
                      'Format: AAAAA9999A (5 letters, 4 digits, 1 letter)'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    inputProps={{
                      maxLength: 10,
                      style: { textTransform: 'uppercase' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {form.idProofFile ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}
                        >
                          {getFileIcon(form.idProofFile)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {form.idProofFile.name}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatFileSize(form.idProofFile.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => handleRemoveFile('idProofFile')}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label='Uploaded'
                        size='small'
                        color='success'
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  ) : existingImageUrls.panImageUrl ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}
                        >
                          <ImageIcon />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Existing PAN Image
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            onClick={() =>
                              handleOpenImagePreview(
                                existingImageUrls.panImageUrl,
                                'Existing PAN Image',
                              )
                            }
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#536DFE',
                              },
                            }}
                          >
                            Click to view
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={() =>
                              handleDownloadExistingImage(
                                existingImageUrls.panImageUrl,
                                'Existing PAN Image',
                              )
                            }
                            title='Download image'
                          >
                            <DownloadIcon fontSize='small' />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() =>
                              handleRemoveExistingImage('panImage')
                            }
                            title='Delete image'
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <img
                          src={existingImageUrls.panImageUrl}
                          alt='PAN Card'
                          style={{
                            width: '100%',
                            maxHeight: '150px',
                            objectFit: 'contain',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            handleOpenImagePreview(
                              existingImageUrls.panImageUrl,
                              'Existing PAN Image',
                            )
                          }
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label='Existing'
                        size='small'
                        color='success'
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  ) : (
                    <Button
                      variant='outlined'
                      component='label'
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        height: 120,
                        borderRadius: 2,
                        textTransform: 'none',
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        borderColor: '#536DFE',
                        color: '#536DFE',
                        flexDirection: 'column',
                        gap: 1,
                        '&:hover': {
                          borderColor: '#536DFE',
                          backgroundColor: '#f3f4ff',
                        },
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 32 }} />
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Upload PAN Card
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Max 5MB (Image/PDF)
                      </Typography>
                      <input
                        type='file'
                        hidden
                        accept='image/*,.pdf'
                        onChange={(e) =>
                          handleFileChange('idProofFile', e.target.files[0])
                        }
                      />
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider textAlign='left' sx={{ mb: 2, mt: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Bank Details
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Bank Name'
                    select
                    fullWidth
                    value={form.bankName}
                    onChange={(e) => handleChange('bankName', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {banks.map((bank) => (
                      <MenuItem key={bank.id} value={bank.value}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Account Holder Name'
                    fullWidth
                    value={form.accountHolderName}
                    onChange={(e) =>
                      handleChange('accountHolderName', e.target.value)
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Account Number'
                    fullWidth
                    value={form.accountNumber}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 18);
                      handleChange('accountNumber', value);
                      setAccountNumberError(validateAccountNumber(value));
                    }}
                    onBlur={(e) => {
                      setAccountNumberError(
                        validateAccountNumber(form.accountNumber),
                      );
                    }}
                    error={!!accountNumberError}
                    helperText={
                      accountNumberError || '8-18 digit account number'
                    }
                    inputProps={{
                      maxLength: 18,
                      inputMode: 'numeric',
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='IFSC Code'
                    fullWidth
                    value={form.ifscCode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().slice(0, 11);
                      handleChange('ifscCode', value);
                      setIfscError(validateIfscCode(value));
                    }}
                    onBlur={(e) => {
                      setIfscError(validateIfscCode(form.ifscCode));
                    }}
                    error={!!ifscError}
                    helperText={
                      ifscError || '11 characters (e.g., SBIN0001234)'
                    }
                    inputProps={{
                      maxLength: 11,
                      style: { textTransform: 'uppercase' },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label='Branch '
                    fullWidth
                    value={form.branch}
                    onChange={(e) => handleChange('branch', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {form.bankDocFile ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}
                        >
                          {getFileIcon(form.bankDocFile)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {form.bankDocFile.name}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatFileSize(form.bankDocFile.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => handleRemoveFile('bankDocFile')}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label='Uploaded'
                        size='small'
                        color='success'
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  ) : existingImageUrls.bankDocFileUrl ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}
                        >
                          <DocumentIcon />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Existing Bank Document
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            onClick={() =>
                              handleOpenImagePreview(
                                existingImageUrls.bankDocFileUrl,
                                'Existing Bank Document',
                              )
                            }
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#536DFE',
                              },
                            }}
                          >
                            Click to view
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={() =>
                              handleDownloadExistingImage(
                                existingImageUrls.bankDocFileUrl,
                                'Existing Bank Document',
                              )
                            }
                            title='Download document'
                          >
                            <DownloadIcon fontSize='small' />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() =>
                              handleRemoveExistingImage('bankDocFile')
                            }
                            title='Delete document'
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label='Existing'
                        size='small'
                        color='success'
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  ) : (
                    <Button
                      variant='outlined'
                      component='label'
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        height: 120,
                        borderRadius: 2,
                        textTransform: 'none',
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        borderColor: '#536DFE',
                        color: '#536DFE',
                        flexDirection: 'column',
                        gap: 1,
                        '&:hover': {
                          borderColor: '#536DFE',
                          backgroundColor: '#f3f4ff',
                        },
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 32 }} />
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Upload Bank Document
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Max 5MB (Image/PDF)
                      </Typography>
                      <input
                        type='file'
                        hidden
                        accept='image/*,.pdf'
                        onChange={(e) =>
                          handleFileChange('bankDocFile', e.target.files[0])
                        }
                      />
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} id='field-terms'>
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.terms}
                          onChange={(e) =>
                            handleChange('terms', e.target.checked)
                          }
                          required
                        />
                      }
                      label={
                        <Typography variant='body2'>
                          I hereby declare that all the information provided
                          above is true and correct to the best of my knowledge.
                          I accept all terms and conditions.
                        </Typography>
                      }
                    />
                    {step2Errors.terms && (
                      <Typography
                        variant='caption'
                        color='error'
                        sx={{ mt: 1, display: 'block' }}
                      >
                        {step2Errors.terms}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Box display='flex' justifyContent='space-between' gap={2} mt={4}>
                <Button
                  variant='outlined'
                  onClick={handleBack}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                >
                  Back
                </Button>
                {canWriteAssociate && (
                  <Button
                    variant='contained'
                    type='submit'
                    disabled={!form.terms || submitting}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 4,
                      boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    }}
                  >
                    {submitting
                      ? editingAssociate
                        ? 'Submitting Changes...'
                        : 'Submitting...'
                      : editingAssociate
                        ? 'Submit Changes'
                        : 'Submit Registration'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </form>
      <Dialog
        open={addCityDialogOpen}
        onClose={handleCloseAddCityDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Add New City</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='State'
                value={form.state}
                disabled
                helperText='City will be added to this state'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='City Name'
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCityDialog} disabled={addingCity}>
            Cancel
          </Button>
          <Button
            onClick={handleAddCity}
            variant='contained'
            disabled={!newCityName.trim() || addingCity}
          >
            {addingCity ? 'Adding...' : 'Add City'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h6'>{previewImageTitle}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {previewImageUrl && !previewImageError && !previewImageLoading && (
              <IconButton
                onClick={handleDownloadImage}
                sx={{ color: 'white' }}
                title='Download image'
              >
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton
              onClick={handleCloseImagePreview}
              sx={{ color: 'white' }}
              title='Close'
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            minHeight: '400px',
          }}
        >
          {previewImageLoading && !previewImageError && (
            <CircularProgress sx={{ color: 'white' }} />
          )}
          {previewImageError && (
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Failed to load image
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}
              >
                The image could not be loaded. Please check the URL or try
                again.
              </Typography>
              <Button
                variant='outlined'
                onClick={() => {
                  setPreviewImageError(false);
                  setPreviewImageLoading(true);
                  // Force reload by creating a new image element
                  const img = new Image();
                  img.onload = () => {
                    setPreviewImageLoading(false);
                    setPreviewImageError(false);
                  };
                  img.onerror = () => {
                    setPreviewImageLoading(false);
                    setPreviewImageError(true);
                  };
                  img.src = previewImageUrl;
                }}
                sx={{ color: 'white', borderColor: 'white' }}
              >
                Retry
              </Button>
            </Box>
          )}
          {previewImageUrl && !previewImageError && (
            <img
              src={previewImageUrl}
              alt={previewImageTitle}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: '8px',
                display: previewImageLoading ? 'none' : 'block',
              }}
              onLoad={() => {
                setPreviewImageLoading(false);
                setPreviewImageError(false);
              }}
              onError={(e) => {
                setPreviewImageLoading(false);
                setPreviewImageError(true);
                console.error('Failed to load image:', previewImageUrl);
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={handleCloseImagePreview}
            variant='contained'
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
        disableRestoreFocus={true}
      />

      <Dialog
        open={confirmDesignationOpen}
        onClose={() => setConfirmDesignationOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pt: 2.5,
            pb: 1,
            bgcolor: '#f0f7ff',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '50%',
              p: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              display: 'flex',
              mb: -4,
              zIndex: 1,
            }}
          >
            <InfoOutlined sx={{ fontSize: 40, color: 'primary.main' }} />
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 4.5, pb: 1, textAlign: 'center' }}>
          <Typography
            variant='h6'
            component='h2'
            sx={{
              fontWeight: 800,
              mb: 1,
              color: 'text.primary',
              letterSpacing: '-0.02em',
            }}
          >
            Confirm Designation
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', lineHeight: 1.5 }}
          >
            Are you sure you have selected <strong>{form.designation}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => setConfirmDesignationOpen(false)}
            variant='outlined'
            fullWidth
            size='small'
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmDesignation}
            fullWidth
            size='small'
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    </Box>
  );
};

export default AssociateRegistration;
